import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { getLenis } from '../../utils/smoothScroll';
import { 
  Sun, 
  Moon, 
  Menu, 
  X, 
  ChevronRight, 
  Github, 
  Twitter, 
  Linkedin, 
  ArrowRight, 
  Check, 
  BarChart2, 
  Calendar, 
  Users, 
  Clock, 
  Shield,
  MessageCircle,
  PanelRight,
  Zap,
  Sparkles,
  Star,
  Layout,
  Play,
  Lock,
  Gem,
  Lightbulb,
  Heart,
  RefreshCw,
  Globe,
  ChevronsRight,
  Package,
  AlertTriangle,
  Truck,
  QrCode
} from 'lucide-react';

const LandingPage = () => {
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  // Fiyatlandırma simülasyonu için state değişkenleri
  const [selectedProduct, setSelectedProduct] = useState('complete-suite');
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [userCount, setUserCount] = useState(5);
  
  // Ürün bilgileri
  const productData = {
    'task-management': {
      name: 'İş Takip Sistemi',
      basePrice: 299,
      userPrice: 50,
      description: 'Projelerinizi ve görevlerinizi yönetin'
    },
    'stock-management': {
      name: 'Stok Yönetimi Sistemi',
      basePrice: 349,
      userPrice: 60,
      description: 'Envanterinizi takip edin'
    },
    'complete-suite': {
      name: 'Tam Paket (İkisi Bir Arada)',
      basePrice: 499,
      userPrice: 80,
      description: 'İş ve stok yönetimini birleştirin'
    }
  };
  
  // Seçilen ürüne göre fiyat hesaplama
  const calculatePrice = () => {
    const product = productData[selectedProduct];
    const additionalUsersCost = Math.max(0, userCount - 3) * product.userPrice;
    let totalPrice = product.basePrice + additionalUsersCost;
    
    // Yıllık plan seçiliyse 2 ay ücretsiz (%16.67 indirim)
    if (billingPeriod === 'yearly') {
      totalPrice = totalPrice * 10; // 12 ay yerine 10 ay ödeme (2 ay ücretsiz)
    }
    
    return {
      basePrice: product.basePrice,
      totalPrice: totalPrice,
      perUserPrice: totalPrice / userCount,
      discountPercentage: billingPeriod === 'yearly' ? 16.67 : 0,
      period: billingPeriod === 'yearly' ? 'yıl' : 'ay'
    };
  };
  
  // Fiyat bilgileri
  const priceDetails = calculatePrice();
  
  // Ürün seçimi değişikliği
  const handleProductChange = (productId) => {
    setSelectedProduct(productId);
  };
  
  // Fatura dönemi değişikliği
  const handleBillingPeriodChange = (period) => {
    setBillingPeriod(period);
  };
  
  // Kullanıcı sayısı değişikliği
  const handleUserCountChange = (e) => {
    setUserCount(Number(e.target.value));
  };
  
  // Sayaç animasyonu için state'ler
  const [countedValues, setCountedValues] = useState({
    users: 0,
    customers: 0,
    satisfaction: 0
  });
  
  // Sayaç animasyonu için referans değerleri
  const targetValues = {
    users: 5000,
    customers: 500,
    satisfaction: 98
  };
  
  // Animasyon başlatıldı mı kontrolü için ref
  const animationTriggered = useRef(false);
  
  // Sayaç animasyonu için inView durumunu kontrol etmek için ref
  const statsRef = useRef(null);
  
  // Lenis scroll bilgisi için state
  const [scrollProgress, setScrollProgress] = useState(0);
  
  // Parallax referansları
  const heroRef = useRef(null);
  const featuresRef = useRef(null);
  const ctaRef = useRef(null);
  
  // Tema-aware logo URL'leri
  const logoUrls = {
    light: "https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/beetrax_logo_transparent3.png", // Dark tema için beyaz logo
    dark: "https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/beetrax_logo_transparent4.png" // Light tema için siyah logo
  };
  
  // Mevcut tema durumuna göre logo URL'sini seç
  const currentLogo = isDark ? logoUrls.light : logoUrls.dark;
  
  // Scroll durumunu izle ve gelişmiş paralaks efektleri için Lenis ile entegre et
  useEffect(() => {
    const handleScroll = (e) => {
      // Mevcut scroll pozisyonu
      const scrollY = window.scrollY || window.pageYOffset;
      
      // Navbar arka planı için scroll durumu
      setScrolled(scrollY > 50);
      
      // Lenis'ten scroll bilgisini al
      const lenis = getLenis();
      if (lenis) {
        // Sayfa scroll progresini yüzde olarak hesapla (0-1 arası)
        const scrollPercentage = scrollY / (document.body.scrollHeight - window.innerHeight);
        setScrollProgress(scrollPercentage);
      }
      
      // Stats bölümü viewport'a girdiğinde animasyonu başlat
      if (statsRef.current && !animationTriggered.current) {
        const rect = statsRef.current.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          startCountAnimation();
          animationTriggered.current = true;
        }
      }
    };

    // Smooth Scroll Eventi Dinle
    const handleSmoothScroll = (e) => {
      const { position, velocity, direction, limit, progress } = e.detail;
      // Scroll bilgisini kullanarak paralaks efektleri için state'i güncelle
      setScrollProgress(progress);
    };

    // Event listener'ları ekle
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('smoothScroll', handleSmoothScroll);
    
    // Başlangıçta bir kez kontrol et
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('smoothScroll', handleSmoothScroll);
    };
  }, []);
  
  // Sayaç animasyonu fonksiyonu
  const startCountAnimation = () => {
    // Animasyon süresi (ms)
    const animationDuration = 2000;
    // Kaç adımda tamamlanacağı
    const steps = 50;
    // Adım başına geçen süre
    const stepDuration = animationDuration / steps;
    
    let currentStep = 0;
    
    const interval = setInterval(() => {
      currentStep++;
      
      if (currentStep <= steps) {
        // Her adımda değerleri güncelle
        setCountedValues({
          users: Math.floor((targetValues.users / steps) * currentStep),
          customers: Math.floor((targetValues.customers / steps) * currentStep),
          satisfaction: Math.floor((targetValues.satisfaction / steps) * currentStep)
        });
      } else {
        // Animasyon tamamlandığında tam değerleri ata
        setCountedValues({
          users: targetValues.users,
          customers: targetValues.customers,
          satisfaction: targetValues.satisfaction
        });
        clearInterval(interval);
      }
    }, stepDuration);
  };

  // Navbar menü itemları
  const navItems = [
    { name: 'Ana Sayfa', href: '#home' },
    { name: 'Özellikler', href: '#features' },
    { name: 'Fiyatlandırma', href: '#pricing' },
    { name: 'Hakkımızda', href: '#about' },
    { name: 'İletişim', href: '/contact' },
  ];

  // Demo butonu için varyasyon
  const buttonVariants = {
    hover: {
      scale: 1.05,
      transition: {
        duration: 0.2,
        yoyo: Infinity,
      },
    },
    tap: {
      scale: 0.95,
    },
  };

  // Daha etkileyici animasyon varyantları
  const textVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: {
        staggerChildren: 0.03
      } 
    }
  };

  const letterVariants = {
    hidden: { 
      opacity: 0,
      y: 20,
      scale: 0.9
    },
    visible: { 
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20
      } 
    }
  };

  // Modern, yüksek kaliteli shimmer efekti
  const shimmer = {
    hidden: { backgroundPosition: "100% 0" },
    visible: { 
      backgroundPosition: "-100% 0",
      transition: {
        repeat: Infinity,
        duration: 5,
        ease: [0.42, 0, 0.58, 1] // Cubic bezier for smoother animation
      }
    }
  };

  // Dinamik ışık efekti için animasyon
  const glowAnimation = {
    initial: { 
      opacity: 0.5,
      scale: 0.95
    },
    animate: {
      opacity: [0.5, 0.8, 0.5],
      scale: [0.95, 1.05, 0.95],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  // Ana başlık için daha güçlü animasyon
  const AnimatedTitle = ({ text, className }) => {
    return (
      <motion.span
        variants={textVariants}
        initial="false"
        animate="visible"
        className={className}
      >
        {text.split('').map((char, index) => (
          <motion.span
            key={index}
            variants={letterVariants}
            className="inline-block"
          >
            {char === ' ' ? '\u00A0' : char}
          </motion.span>
        ))}
      </motion.span>
    );
  };

  // Modern etkileyici Shimmer Text komponenti
  const ShimmerText = ({ children, className = "" }) => {
    return (
      <motion.span
        initial="hidden"
        animate="visible"
        variants={shimmer}
        style={{
          backgroundSize: "200% 100%",
          backgroundClip: "text",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundImage: isDark 
            ? "linear-gradient(90deg, #E2E8F0, #F8FAFC, #E2E8F0)" 
            : "linear-gradient(90deg, #0F172A, #334155, #0F172A)"
        }}
        className={`font-bold ${className}`}
      >
        {children}
      </motion.span>
    );
  };

  // Başlık bölümü için güçlü gradient
  const TitleGradient = ({ children, className = "" }) => {
    return (
      <span
        className={`font-bold relative z-10 ${className}`}
        style={{
          background: isDark 
            ? "linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 50%, #CBD5E1 100%)" 
            : "linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #334155 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text"
        }}
      >
        {children}
      </span>
    );
  };

  // Enhanced 3D text effect for corporate headings
  const CorporateHeading = ({ children, className = "" }) => {
    return (
      <span
        className={`font-extrabold relative z-10 ${className}`}
        style={{
          textShadow: isDark 
            ? "0px 1px 1px rgba(255,255,255,0.1), 0px 2px 1px rgba(255,255,255,0.05)" 
            : "0px 1px 1px rgba(0,0,0,0.1), 0px 2px 1px rgba(0,0,0,0.05)",
          letterSpacing: "-0.02em",
        }}
      >
        {children}
      </span>
    );
  };

  // Enhanced bento variants with more subtle animations for corporate feel
  const corporateBentoVariants = {
    hidden: { 
      opacity: 0, 
      y: 15,
    },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        duration: 0.5
      }
    },
    hover: {
      y: -3,
      boxShadow: isDark 
        ? "0 15px 30px -8px rgba(0, 0, 0, 0.3)" 
        : "0 15px 30px -10px rgba(0, 0, 0, 0.08)",
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 25,
        duration: 0.4
      }
    }
  };

  // Bento box tarzı kart animasyonu
  const bentoBoxVariants = {
    hidden: { 
      opacity: 0, 
      y: 20,
    },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 30
      }
    },
    hover: {
      y: -5,
      boxShadow: isDark 
        ? "0 20px 25px -5px rgba(0, 0, 0, 0.4)" 
        : "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 15
      }
    }
  };

  // Bento kartları için ortak stiller
  const bentoCardClasses = (size = "normal") => {
    const baseClasses = `rounded-3xl transition-all duration-300 ${
      isDark 
        ? 'bg-slate-800/40 backdrop-blur-xl border border-slate-700/50' 
        : 'bg-white/80 backdrop-blur-xl border border-slate-200/50 shadow-[0_8px_30px_rgb(0,0,0,0.04)]'
    } group relative overflow-hidden`;
    
    // Farklı kart boyutları için padding ve min-height değerleri
    const sizeClasses = {
      small: "p-5 min-h-[200px]",
      normal: "p-6 min-h-[250px]",
      large: "p-8 min-h-[300px]",
      tall: "p-8 min-h-[450px]"
    };
    
    return `${baseClasses} ${sizeClasses[size]}`;
  };

  // Hero Bölümü - Paralaks efekti ekle
  const heroParallaxVariants = {
    container: {
      initial: { opacity: 0 },
      animate: { opacity: 1, transition: { staggerChildren: 0.1 } }
    },
    title: {
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    },
    description: {
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0, transition: { duration: 0.8, delay: 0.2, ease: "easeOut" } }
    },
    button: {
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0, transition: { duration: 0.8, delay: 0.4, ease: "easeOut" } }
    },
    image: {
      initial: { opacity: 0, scale: 0.95 },
      animate: { opacity: 1, scale: 1, transition: { duration: 1, delay: 0.3, ease: "easeOut" } }
    }
  };

  // Yeni ProductOption ve FeatureRow bileşenleri
  const ProductOption = ({ id, name, description, icon, price, isDark, recommended }) => (
    <label 
      className={`block relative p-8 border rounded-xl cursor-pointer transition-all ${
        recommended 
          ? (isDark ? 'bg-slate-700/70 border-blue-500/70' : 'bg-white border-blue-500/70')
          : (isDark ? 'bg-slate-700/30 border-slate-600/50 hover:border-slate-500' : 'bg-white border-slate-200 hover:border-slate-300')
      } ${selectedProduct === id ? 'ring-2 ring-blue-500' : ''}`}
    >
      <input 
        type="radio" 
        name="product" 
        id={id}
        className="sr-only peer"
        checked={selectedProduct === id}
        onChange={() => handleProductChange(id)}
      />
      <div className="flex items-start">
        <div className={`flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-lg mr-3 ${
          isDark ? 'bg-slate-600' : 'bg-slate-100'
        }`}>
          {icon}
        </div>
        
        <div className="flex-grow">
          <div className="flex justify-between items-start">
            <div>
              <div className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {name}
              </div>
              <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                {description}
              </div>
            </div>
            
            <div className={`text-lg font-semibold mr-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              ₺{price}
            </div>
          </div>
        </div>
      </div>
      
      <div className={`absolute top-1/2 transform -translate-y-1/2 right-4 ${selectedProduct === id ? 'flex' : 'hidden'} items-center justify-center h-6 w-6 rounded-full bg-blue-500 text-white`}>
        <Check className="w-4 h-4" />
      </div>
      
      {recommended && (
        <div className="absolute -top-3 -right-1">
          <div className={`py-1 px-3 rounded-full text-xs font-medium ${
            isDark ? 'bg-blue-500 text-white' : 'bg-blue-100 text-blue-800'
          }`}>
            Önerilen
          </div>
        </div>
      )}
    </label>
  );

  const FeatureRow = ({ feature, taskManagement, stockManagement, completeSuite, isDark }) => (
    <tr className={isDark ? 'border-b border-slate-800' : 'border-b border-slate-100'}>
      <td className="py-3 px-4">
        {feature}
      </td>
      <td className="py-3 px-4 text-center">
        {taskManagement ? (
          <Check className={`w-5 h-5 mx-auto ${isDark ? 'text-green-400' : 'text-green-500'}`} />
        ) : (
          <X className={`w-5 h-5 mx-auto ${isDark ? 'text-slate-600' : 'text-slate-400'}`} />
        )}
      </td>
      <td className="py-3 px-4 text-center">
        {stockManagement ? (
          <Check className={`w-5 h-5 mx-auto ${isDark ? 'text-green-400' : 'text-green-500'}`} />
        ) : (
          <X className={`w-5 h-5 mx-auto ${isDark ? 'text-slate-600' : 'text-slate-400'}`} />
        )}
      </td>
      <td className="py-3 px-4 text-center">
        {completeSuite ? (
          <Check className={`w-5 h-5 mx-auto ${isDark ? 'text-green-400' : 'text-green-500'}`} />
        ) : (
          <X className={`w-5 h-5 mx-auto ${isDark ? 'text-slate-600' : 'text-slate-400'}`} />
        )}
      </td>
    </tr>
  );

  return (
    <div className="min-h-screen antialiased overflow-x-hidden">
      {/* Paralaks arkaplan */}
      <div 
        className="fixed inset-0 w-full h-full z-0 pointer-events-none"
        style={{
          transform: `translateY(${scrollProgress * 300}px)`,
          opacity: 1 - scrollProgress * 1.5
        }}
      >
        <div className={`absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:30px_30px] ${isDark ? 'opacity-8' : 'opacity-15'}`}></div>
        
        {/* Parallax ile hareket eden parçacıklar */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className={`absolute rounded-full ${isDark ? 'bg-slate-500/15' : 'bg-slate-500/10'}`}
              style={{
                width: Math.random() * 8 + 2 + 'px',
                height: Math.random() * 8 + 2 + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
                // Scroll ile farklı hızlarda hareket etsin
                transform: `translateY(${scrollProgress * 100 * (1 + Math.random())}px)`,
              }}
              animate={{
                y: [0, -20, 0],
                opacity: [0.2, 0.4, 0.2],
              }}
              transition={{
                duration: Math.random() * 6 + 10,
                repeat: Infinity,
                delay: Math.random() * 5,
              }}
            />
          ))}
        </div>
      </div>

      {/* Enhanced Navbar with glow effect */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-in-out ${
          scrolled 
            ? (isDark ? 'bg-gray-900/60 backdrop-blur-xl border-b border-white/10 shadow-lg shadow-blue-500/5' : 'bg-white/60 backdrop-blur-xl border-b border-gray-200/50 shadow-lg shadow-blue-500/10') 
            : 'bg-transparent'
        }`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo with subtle animation */}
            <motion.div 
              className="flex items-center space-x-2"
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <a href="#" className="cursor-pointer">
                <img 
                  src={currentLogo} 
                  alt="Beetrax Logo" 
                  className="w-30 h-5 object-contain"
                />
              </a>
            </motion.div>

            {/* Desktop Navigation with subtle hover effect */}
            <nav className="hidden md:flex items-center space-x-8">
              {navItems.map((item, index) => (
                <a 
                  key={index} 
                  href={item.href}
                  className={`text-sm font-medium relative overflow-hidden group ${isDark ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'}`}
                >
                  {item.name}
                  <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-slate-400 to-slate-600 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300"></span>
                </a>
              ))}
            </nav>

            {/* Professional Action Buttons */}
            <div className="hidden md:flex items-center space-x-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={toggleTheme}
                className={`p-2 rounded-full transition-colors ${
                  isDark 
                    ? 'bg-gray-800 text-yellow-400 border border-gray-700' 
                    : 'bg-gray-100 text-gray-700 border border-gray-200'
                }`}
              >
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/signin')}
                className={`py-2 px-5 rounded-lg text-sm font-medium transition-all ${
                  isDark 
                    ? 'bg-white/10 text-white border border-white/20 backdrop-blur-md hover:bg-white/15' 
                    : 'bg-white text-gray-800 border border-gray-300 shadow-sm hover:bg-gray-50'
                }`}
              >
                Giriş Yap
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/register')}
                className={`py-2 px-5 rounded-lg text-sm font-medium transition-all 
                  ${isDark
                    ? 'bg-slate-700 text-white border border-slate-600 shadow-sm hover:bg-slate-600'
                    : 'bg-slate-800 text-white border border-slate-700 shadow-sm hover:bg-slate-700'
                  }`}
              >
                <span className="flex items-center">
                  Kayıt Ol
                </span>
              </motion.button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center space-x-4">
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-full transition-colors ${isDark ? 'bg-gray-800 text-yellow-400 border border-gray-700' : 'bg-gray-100 text-gray-700 border border-gray-200'}`}
              >
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>

              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`p-2 rounded-full ${isDark ? 'bg-gray-800 text-white border border-gray-700' : 'bg-gray-100 text-gray-700 border border-gray-200'}`}
              >
                {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu with improved glassmorphism */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className={`md:hidden overflow-hidden ${isDark ? 'bg-gray-900/80 backdrop-blur-xl border-b border-white/10' : 'bg-white/80 backdrop-blur-xl border-b border-gray-200/50'}`}
            >
              <div className="container mx-auto px-4 py-4 space-y-4">
                {navItems.map((item, index) => (
                  <a
                    key={index}
                    href={item.href}
                    onClick={() => setIsMenuOpen(false)}
                    className={`block py-2 text-sm font-medium ${isDark ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'}`}
                  >
                    {item.name}
                  </a>
                ))}
                <div className="pt-4 space-y-3">
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      navigate('/signin');
                    }}
                    className={`w-full py-2 px-4 rounded-lg text-sm font-medium transition-colors ${isDark ? 'bg-white/10 text-white border border-white/20 hover:bg-white/15' : 'bg-white text-gray-800 border border-gray-300 shadow-sm hover:bg-gray-50'}`}
                  >
                    Giriş Yap
                  </button>
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      navigate('/register');
                    }}
                    className={`w-full py-2 px-4 rounded-lg text-sm font-medium transition-colors ${isDark ? 'bg-slate-700 text-white border border-slate-600 hover:bg-slate-600' : 'bg-slate-800 text-white border border-slate-700 hover:bg-slate-700'}`}
                  >
                    Kayıt Ol
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Hero Section with Parallax */}
      <section id="home"  ref={heroRef} className="relative scroll-section pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        {/* Parallax decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div 
            className="absolute top-[20%] right-[10%] w-80 h-80 rounded-full bg-blue-500/10 blur-3xl"
            style={{
              transform: `translate(${scrollProgress * 100}px, ${-scrollProgress * 200}px)`,
              opacity: 0.5 - scrollProgress * 0.3
            }}
          ></div>
          <div 
            className="absolute bottom-[10%] left-[5%] w-60 h-60 rounded-full bg-purple-500/10 blur-3xl"
            style={{
              transform: `translate(${-scrollProgress * 150}px, ${scrollProgress * 100}px)`,
              opacity: 0.5 - scrollProgress * 0.3
            }}
          ></div>
        </div>
      
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 mb-72 mt-24">
          {/* Asimetrik hero layout - iki kolonlu yapı */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            {/* Sol kısım - Başlık ve açıklama, CTA butonları */}
            <div className="relative">
              {/* Dekoratif çizgiler ve elementler */}
              <div className="absolute -left-10 top-10 h-40 w-40 opacity-10">
                <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className={`${isDark ? 'text-blue-300' : 'text-blue-500'}`}>
                  <path fill="currentColor" d="M45.3,-61.2C58.5,-47.9,69.4,-33.1,73.7,-16.5C78,0.1,75.7,18.5,67.2,33.2C58.7,47.8,43.9,58.5,27.4,65.2C10.9,71.9,-7.4,74.4,-25.5,70.2C-43.7,65.9,-61.8,55,-68.8,39.4C-75.8,23.8,-71.7,3.5,-66.4,-15.7C-61.1,-35,-54.7,-53.1,-42.4,-66.5C-30.1,-79.9,-12.1,-88.6,3.2,-92.5C18.6,-96.4,32.1,-74.5,45.3,-61.2Z" transform="translate(100 100)" />
                </svg>
              </div>

              {/* Bold, modern heading with powerful styling */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="relative z-10"
              >
                {/* Arka planda hafif ışık efekti */}
                <div className={`absolute -left-10 top-0 transform -translate-y-1/2 w-40 h-40 rounded-full ${
                  isDark ? 'bg-blue-700/10' : 'bg-blue-200/40'
                } filter blur-3xl opacity-70`}></div>

                <h1 className={`text-6xl lg:text-7xl xl:text-8xl font-black tracking-tight leading-tight mb-8 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <div className="overflow-hidden">
                    <motion.div
                      initial={{ y: 100 }}
                      animate={{ y: 0 }}
                      transition={{ duration: 0.8, ease: [0.33, 1, 0.68, 1] }}
                    >
                      <span className={`inline-block ${isDark ? 'text-white' : 'text-gray-900'}`}>İş</span>{" "}
                      <span className={`inline-block ${isDark ? 'text-white' : 'text-gray-900'}`}>Süreçlerinizi</span>
                    </motion.div>
                  </div>
                  <div className="overflow-hidden mt-2">
                    <motion.div
                      initial={{ y: 100 }}
                      animate={{ y: 0 }}
                      transition={{ duration: 0.8, delay: 0.1, ease: [0.33, 1, 0.68, 1] }}
                      className="flex items-center"
                    >
                      <div className="relative">
                        <TitleGradient className="z-10 relative text-6xl lg:text-7xl">Yeniden</TitleGradient>
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: "100%" }}
                          transition={{ duration: 1, delay: 1.2 }}
                          className={`absolute left-0 right-0 -bottom-2 h-1 rounded-full ${
                            isDark 
                              ? 'bg-gradient-to-r from-blue-700 via-blue-400 to-blue-700' 
                              : 'bg-gradient-to-r from-blue-700 via-blue-500 to-blue-700'
                          }`}
                        />
                      </div>
                      <div className="ml-4 relative">
                        <ShimmerText className="z-10 relative text-6xl lg:text-7xl">Tanımlayın</ShimmerText>
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: "100%" }}
                          transition={{ duration: 1, delay: 1.3 }}
                          className={`absolute left-0 right-0 -bottom-2 h-1 rounded-full ${
                            isDark 
                              ? 'bg-gradient-to-r from-purple-700 via-purple-400 to-purple-700' 
                              : 'bg-gradient-to-r from-purple-700 via-purple-500 to-purple-700'
                          }`}
                        />
                      </div>
                    </motion.div>
                  </div>
                </h1>
                
                {/* More powerful subtitle with highlighting and staggered entrance */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.6 }}
                >
                  <p className={`text-xl md:text-2xl mb-10 ${isDark ? 'text-gray-300' : 'text-gray-600'} max-w-xl leading-relaxed`}>
                    Modern iş yönetim platformumuzla{" "}
                    <motion.span
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 1.0 }}
                      className={`inline-block font-medium px-2 py-0.5 rounded ${isDark ? 'bg-slate-800 text-blue-300' : 'bg-blue-50 text-blue-800 border border-blue-100'}`}
                    >
                      <span className="relative">
                        otomatikleştirin
                        <motion.span 
                          className="absolute -bottom-0.5 left-0 right-0 h-0.5 bg-blue-400"
                          initial={{ width: "0%" }}
                          animate={{ width: "100%" }}
                          transition={{ duration: 0.3, delay: 1.3 }}
                        />
                      </span>
                    </motion.span>{" "}
                    <motion.span
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 1.1 }}
                      className={`inline-block font-medium px-2 py-0.5 rounded ${isDark ? 'bg-slate-800 text-purple-300' : 'bg-purple-50 text-purple-800 border border-purple-100'}`}
                    >
                      <span className="relative">
                        güçlendirin
                        <motion.span 
                          className="absolute -bottom-0.5 left-0 right-0 h-0.5 bg-purple-400"
                          initial={{ width: "0%" }}
                          animate={{ width: "100%" }}
                          transition={{ duration: 0.3, delay: 1.4 }}
                        />
                      </span>
                    </motion.span>{" "}
                    ve projelerinizi{" "}
                    <motion.span
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 1.2 }}
                      className={`inline-block font-medium px-2 py-0.5 rounded ${isDark ? 'bg-slate-800 text-green-300' : 'bg-green-50 text-green-800 border border-green-100'}`}
                    >
                      <span className="relative">
                        zamanında tamamlayın
                        <motion.span 
                          className="absolute -bottom-0.5 left-0 right-0 h-0.5 bg-green-400"
                          initial={{ width: "0%" }}
                          animate={{ width: "100%" }}
                          transition={{ duration: 0.3, delay: 1.5 }}
                        />
                      </span>
                    </motion.span>{" "}
                  </p>
                </motion.div>
                
                {/* Gelişmiş, etkileyici CTA butonları */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.8 }}
                  className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6"
                >
                  {/* Ana CTA butonu - 3D derinlik efekti ile */}
                  <motion.div
                    className="w-full sm:w-auto relative group"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {/* Buton arkasında ışık efekti */}
                    <div className={`absolute inset-0 rounded-xl ${isDark ? 'bg-blue-600/50' : 'bg-blue-400/50'} blur-md group-hover:blur-xl transition-all duration-300 -z-10 group-hover:opacity-100 opacity-70 group-hover:scale-110 scale-100`}></div>
                    
                    <button
                      onClick={() => navigate('/register?plan=' + selectedProduct + '&period=' + billingPeriod + '&users=' + userCount)}
                      className={`relative w-full sm:w-auto py-4 px-8 rounded-xl text-white font-medium bg-gradient-to-br ${isDark ? 'from-slate-700 via-slate-800 to-slate-900' : 'from-slate-800 via-slate-900 to-black'} shadow-lg flex items-center justify-center overflow-hidden group z-10`}
                    >
                      {/* Işık yansıma efekti */}
                      <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      
                      {/* Buton içeriği */}
                      <div className="relative flex items-center">
                        <div className="absolute -left-3 -top-3 w-10 h-10 rounded-full bg-blue-500/20 filter blur-md"></div>
                        <Sparkles className="w-5 h-5 mr-3 opacity-90" />
                        <span className="font-bold text-lg">Ücretsiz Başla</span>
                        <div className="ml-3 relative">
                          <ChevronRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform duration-300" />
                          <div className="absolute inset-0 w-full h-full bg-white/20 rounded-full filter blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </div>
                      </div>
                    </button>
                  </motion.div>
                  
                  {/* İkincil CTA butonu - Glassmorphism efekti ile */}
                  <motion.button
                    whileHover={{ scale: 1.02, boxShadow: isDark ? "0 0 15px 0 rgba(255, 255, 255, 0.1)" : "0 5px 15px rgba(0, 0, 0, 0.08)" }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate('/demo')}
                    className={`w-full sm:w-auto py-4 px-8 rounded-xl font-bold text-lg flex items-center justify-center backdrop-filter backdrop-blur-lg ${
                      isDark 
                        ? 'bg-white/5 text-white border border-white/10 hover:bg-white/10' 
                        : 'bg-white/70 text-gray-800 border border-gray-200 hover:bg-white'
                    }`}
                  >
                    <div className="mr-3 relative">
                      <motion.div 
                        className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-300/20 to-purple-300/20 filter blur-sm"
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
                      />
                      <Play className="w-5 h-5 relative z-10" />
                    </div>
                    Demo İzle
                  </motion.button>
                </motion.div>
                
                {/* Sosyal kanıt bölümü - Sayısal veriler - Sayaç animasyonu eklenmiş hali */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 1.5 }}
                  className={`mt-12 pt-8 ${isDark ? 'border-t border-white/10' : 'border-t border-gray-200/50'} grid grid-cols-3 gap-2`}
                  ref={statsRef}
                >
                  {[
                    { 
                      value: countedValues.users.toLocaleString() + "+", 
                      target: "5,000+", 
                      label: "Aktif Kullanıcı" 
                    },
                    { 
                      value: countedValues.customers.toLocaleString() + "+", 
                      target: "500+", 
                      label: "Kurumsal Müşteri" 
                    },
                    { 
                      value: "%" + countedValues.satisfaction, 
                      target: "%98", 
                      label: "Müşteri Memnuniyeti" 
                    },
                  ].map((stat, index) => (
                    <div key={index} className="text-center">
                      <motion.p 
                        className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 1.5 + index * 0.1 }}
                      >
                        {stat.value}
                      </motion.p>
                      <motion.p 
                        className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5, delay: 1.8 + index * 0.1 }}
                      >
                        {stat.label}
                      </motion.p>
                    </div>
                  ))}
                </motion.div>
              </motion.div>
            </div>

            {/* Sağ kısım - Gelişmiş dashboard görseli ve 3D efektler */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative lg:h-[35rem] mt-20 lg:mt-0 flex justify-center items-center"
            >
              {/* Görselin etrafındaki dairesel ışık efekti */}
              <motion.div 
                className={`absolute inset-0 rounded-full ${
                  isDark ? 'bg-gradient-to-tr from-blue-700/20 via-transparent to-purple-700/20' : 'bg-gradient-to-tr from-blue-300/20 via-transparent to-purple-300/20'
                } filter blur-3xl opacity-70 z-0`}
                animate={{ 
                  scale: [1, 1.05, 1],
                  opacity: [0.7, 0.9, 0.7] 
                }}
                transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
              />
              
              {/* Ana dashboard görseli - 3D stil */}
              <div className="relative z-10 transform lg:rotate-2 hover:rotate-0 transition-transform duration-500">
                {/* Gölge efekti */}
                <div className={`absolute -bottom-6 inset-x-10 h-10 bg-black/40 filter blur-xl rounded-full z-0 opacity-30`}></div>
                
                {/* Ana görsel */}
                <div className={`relative z-10 rounded-2xl overflow-hidden transform perspective-1000 shadow-2xl ${
                  isDark 
                    ? 'shadow-blue-500/20 ring-1 ring-white/10' 
                    : 'shadow-blue-500/30 ring-1 ring-black/5'
                }`}>
                  {/* Görsel üzerinde parıltı efekti */}
                  <div className="absolute inset-0 bg-gradient-to-tr from-white/10 via-white/5 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 z-20"></div>
                  
                  {/* Dashboard görseli */}
                  <img 
                    src="https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/Ui_designX.webp" 
                    alt="Dashboard Preview" 
                    className="w-full h-auto relative z-10" 
                  />
                  
                  {/* Görsel kenarında ışık efekti */}
                  <div className={`absolute inset-0 rounded-2xl ${
                    isDark 
                      ? 'bg-gradient-to-tr from-blue-600/20 via-transparent to-purple-600/20 opacity-40' 
                      : 'bg-gradient-to-tr from-blue-500/15 via-transparent to-purple-500/15 opacity-40'
                  }`}>
                    <motion.div
                      initial={{ opacity: 0.4 }}
                      animate={{ opacity: 0.7 }}
                      transition={{ duration: 2 }}
                      className="absolute inset-0 rounded-2xl"
                    />
                  </div>
                </div>
                
                {/* Etiketler (Badges) - Modernize edilmiş */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.5 }}
                  className="absolute -left-8 -bottom-8 z-20 transform -rotate-3 hover:rotate-0 transition-transform duration-300" 
                >
                  <div className={`px-5 py-3 rounded-xl text-sm font-medium shadow-xl backdrop-blur-md border border-white/20 
                    ${isDark 
                      ? 'bg-slate-800/60 text-white' 
                      : 'bg-white/60 text-slate-800'
                    } flex items-center`}
                  >
                    <div className={`p-2 rounded-full mr-3 ${isDark ? 'bg-green-500/20' : 'bg-green-500/10'}`}>
                      <Star className={`w-4 h-4 ${isDark ? 'text-green-300' : 'text-green-600'}`} fill={isDark ? "#86efac" : "#16a34a"} />
                    </div>
                    <span className="font-semibold">Yenilikçi Arayüz</span>
                  </div>
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                  className="absolute -right-8 -top-8 z-20 transform rotate-3 hover:rotate-0 transition-transform duration-300"
                >
                  <div className={`px-5 py-3 rounded-xl text-sm font-medium shadow-xl backdrop-blur-md border border-white/20
                    ${isDark 
                      ? 'bg-slate-800/60 text-white' 
                      : 'bg-white/60 text-slate-800'
                    } flex items-center`}
                  >
                    <div className={`p-2 rounded-full mr-3 ${isDark ? 'bg-purple-500/20' : 'bg-purple-500/10'}`}>
                      <Zap className={`w-4 h-4 ${isDark ? 'text-purple-300' : 'text-purple-600'}`} />
                    </div>
                    <span className="font-semibold">Hızlı ve Güvenli</span>
                  </div>
                </motion.div>
                
                {/* Ek etiket - sağ ortada */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.0, duration: 0.5 }}
                  className="absolute right-20 bottom-1/3 z-20 transform rotate-6 hover:rotate-0 transition-transform duration-300"
                >
                  <div className={`px-4 py-2 rounded-xl text-sm font-medium shadow-xl backdrop-blur-md border border-white/20
                    ${isDark 
                      ? 'bg-slate-800/60 text-white' 
                      : 'bg-white/60 text-slate-800'
                    } flex items-center`}
                  >
                    <div className={`p-1.5 rounded-full mr-2 ${isDark ? 'bg-blue-500/20' : 'bg-blue-500/10'}`}>
                      <Shield className={`w-3.5 h-3.5 ${isDark ? 'text-blue-300' : 'text-blue-600'}`} />
                    </div>
                    <span className="font-semibold">%100 Güvenli</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
        
        {/* Dalga şeklindeki alt ayırıcı */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 250" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
            <path 
              fill={isDark ? "#111827" : "#F9FAFB"} 
              fillOpacity="1" 
              d="M0,160L48,149.3C96,139,192,117,288,122.7C384,128,480,160,576,170.7C672,181,768,171,864,149.3C960,128,1056,96,1152,90.7C1248,85,1344,107,1392,117.3L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            ></path>
          </svg>
        </div>
      </section>
      
      {/* Features Section with Modern Bento Grid Style - Enhanced for Corporate */}
      <section ref={featuresRef} className="relative scroll-section py-20 md:py-32 overflow-hidden">
        {/* Improved background effects for the features section */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {/* Improved background effects for the features section */}
          <div className={`absolute bottom-0 left-1/4 w-96 h-96 rounded-full ${
            isDark ? 'bg-slate-700/20' : 'bg-slate-300/30'
          } filter blur-[100px] opacity-60`}></div>
          <div className={`absolute top-1/3 right-1/4 w-80 h-80 rounded-full ${
            isDark ? 'bg-slate-700/20' : 'bg-slate-300/30'
          } filter blur-[100px] opacity-50`}></div>
          
          {/* Corporate pattern overlay */}
          <div id="features" className="absolute inset-0 opacity-5 bg-[radial-gradient(circle_at_center,rgba(120,120,120,0.1)_1px,transparent_1px)] bg-[size:28px_28px]"></div>
        </div>
        
        <div  className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Enhanced Bento Grid Header with 3D text effects */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.7 }}
            className="max-w-4xl mx-auto text-center mb-20"
          >
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5 }}
              className="mb-2"
            >
              <span className={`inline-block px-4 py-1 rounded-full text-sm font-medium ${
                isDark ? 'bg-slate-800/80 text-slate-200 ring-1 ring-slate-700/30' : 'bg-slate-100 text-slate-700 ring-1 ring-slate-200/70'
              }`}>Enterprise-Grade Solutions</span>
            </motion.div>
            
            <h2 className={`text-3xl md:text-5xl font-bold mb-6 leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <CorporateHeading>
                Modern İş Yönetimi İçin{" "}
              </CorporateHeading>
              <br className="hidden md:block" />
              <span className="relative inline-block mt-1">
                <TitleGradient className="text-3xl md:text-5xl">Kurumsal Çözümler</TitleGradient>
                <motion.span 
                  className={`absolute -bottom-2 left-0 right-0 h-1 ${
                    isDark ? 'bg-gradient-to-r from-slate-500 via-slate-400 to-slate-500' : 'bg-gradient-to-r from-slate-700 via-slate-900 to-slate-700'
                  } rounded-full`}
                  initial={{ width: "0%" }}
                  whileInView={{ width: "100%" }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: 0.5 }}
                ></motion.span>
              </span>
            </h2>
            
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className={`text-lg md:text-xl max-w-3xl mx-auto ${isDark ? 'text-slate-300' : 'text-slate-600'} leading-relaxed`}
            >
              Şirketinizin iş süreçlerini ve stok yönetimini optimize etmek için tasarlanmış 
              <span className={`mx-1 font-medium ${isDark ? 'text-slate-100' : 'text-slate-800'}`}>
                profesyonel çözümler
              </span> 
              ile operasyonel verimliliğinizi artırın.
            </motion.p>
          </motion.div>
          
          {/* True Bento Grid Layout - Enhanced with corporate style */}
          <div className="grid grid-cols-12 gap-4 md:gap-5 lg:gap-6 auto-rows-auto">
            
            {/* Feature 1: Analytics - Tall Card (2x2) - Enhanced */}
            <motion.div
              variants={corporateBentoVariants}
              initial="hidden"
              whileInView="visible"
              whileHover="hover"
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4 }}
              className={`col-span-12 sm:col-span-6 lg:col-span-4 row-span-2 ${bentoCardClasses("tall")} flex flex-col justify-between`}
            >
              {/* Glass highlight effect */}
              <div className="absolute inset-0 bg-gradient-to-tr from-white/[0.05] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              
              {/* Accent soft glow */}
              <div className={`absolute -top-20 -right-20 w-40 h-40 rounded-full ${
                isDark ? 'bg-slate-500/20' : 'bg-slate-300/15'
              } blur-3xl group-hover:bg-slate-500/25 transition-colors duration-300`}></div>
              
              {/* Modern geometric detail */}
              <div className="absolute -bottom-24 -left-24 w-48 h-48 border border-slate-500/10 rounded-full"></div>
              <div className="absolute -bottom-20 -left-20 w-40 h-40 border border-slate-500/10 rounded-full"></div>
              
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-6">
                  <div className={`flex items-center justify-center w-14 h-14 rounded-2xl ${
                    isDark 
                      ? 'bg-gradient-to-br from-slate-700 to-slate-800 shadow-lg shadow-slate-900/20' 
                      : 'bg-gradient-to-br from-slate-200 to-slate-300 shadow-lg shadow-slate-500/10'
                  }`}>
                    <BarChart2 className={`w-6 h-6 ${isDark ? 'text-slate-300' : 'text-slate-700'}`} />
                  </div>
                  
                  {/* Status badge */}
                  <div className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                    isDark ? 'bg-slate-700/70 text-slate-300 ring-1 ring-slate-600/40' : 'bg-slate-100 text-slate-700 ring-1 ring-slate-200/70'
                  }`}>
                    Enterprise Ready
                  </div>
                </div>
                
                <h3 className={`text-2xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'} group-hover:translate-x-1 transition-transform duration-300`}>
                  <div className="flex items-center space-x-1">
                    <span>Gerçek Zamanlı</span>
                    <span className="relative">
                      <TitleGradient>Analitikler</TitleGradient>
                      <motion.span 
                        className={`absolute -bottom-1 left-0 right-0 h-[2px] ${
                          isDark ? 'bg-gradient-to-r from-slate-500/0 via-slate-500 to-slate-500/0' : 'bg-gradient-to-r from-slate-300/0 via-slate-500 to-slate-300/0'
                        }`}
                        initial={{ width: "0%" }}
                        whileInView={{ width: "100%" }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.5 }}
                      ></motion.span>
                    </span>
                  </div>
                </h3>
                
                <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-6 leading-relaxed`}>
                  Karar alma süreçlerinizi güçlendiren, üst düzey <strong>veri görselleştirme</strong> ve iş analitiği araçları. Kurumsal raporlama ölçeğinde, kesintisiz performans izleme.
                </p>
                
                <div className={`mt-auto p-5 rounded-xl ${
                  isDark ? 'bg-slate-800/70 ring-1 ring-slate-700/80' : 'bg-slate-50/90 ring-1 ring-slate-200/70'
                } backdrop-blur-sm`}>
                  <h4 className={`text-sm font-semibold mb-3 ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                    Öne Çıkan Özellikler
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                    {[
                      "Kurumsal raporlama", 
                      "Performans izleme",
                      "KPI göstergeleri", 
                      "Otomatik bildirimler"
                    ].map((feature, i) => (
                      <div key={i} className="flex items-center space-x-2">
                        <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                          isDark ? 'bg-slate-700' : 'bg-white'
                        }`}>
                          <Check className={`w-3 h-3 ${isDark ? 'text-slate-300' : 'text-slate-700'}`} />
                        </div>
                        <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-3 border-t border-slate-700/20">
                    <div className="flex items-center justify-between">
                      <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        Desteklenen formatlar
                      </span>
                      <div className="flex space-x-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-200 text-slate-700'}`}>
                          CSV
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-200 text-slate-700'}`}>
                          PDF
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-200 text-slate-700'}`}>
                          XLS
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* Feature 2: Task Management - Enhanced */}
            <motion.div
              variants={corporateBentoVariants}
              initial="hidden"
              whileInView="visible"
              whileHover="hover"
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className={`col-span-12 sm:col-span-6 lg:col-span-4 row-span-1 ${bentoCardClasses("normal")}`}
            >
              {/* Subtle pattern background */}
              <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle_at_10%_20%,rgba(120,120,120,0.2)_1px,transparent_1px)] bg-[size:20px_20px]"></div>
              
              <div className={`absolute -top-20 -left-20 w-40 h-40 rounded-full ${
                isDark ? 'bg-slate-500/20' : 'bg-slate-300/15'
              } blur-3xl group-hover:bg-slate-500/25 transition-colors duration-300`}></div>
              
              <div className="relative z-10 flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className={`flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-xl ${
                    isDark 
                      ? 'bg-gradient-to-br from-slate-700 to-slate-800 shadow-lg shadow-slate-900/20' 
                      : 'bg-gradient-to-br from-slate-200 to-slate-300 shadow-lg shadow-slate-500/10'
                  }`}>
                    <Calendar className={`w-5 h-5 ${isDark ? 'text-slate-300' : 'text-slate-700'}`} />
                  </div>
                  
                  <div>
                    <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'} group-hover:translate-x-1 transition-transform duration-300`}>
                      <TitleGradient>Akıllı Görev Yönetimi</TitleGradient>
                    </h3>
                    
                    <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} max-w-sm`}>
                      Otomatik önceliklendirme ve gelişmiş etiketleme sistemi ile iş akışlarınızı optimize edin.
                    </p>
                    
                    <div className="flex flex-wrap gap-2 mt-3">
                      <span className={`inline-flex items-center text-xs font-medium px-2 py-1 rounded ${
                        isDark ? 'bg-slate-700/70 text-slate-300' : 'bg-slate-100 text-slate-700'
                      }`}>
                        <Check className="w-3 h-3 mr-1" /> Akıllı önceliklendirme
                      </span>
                      <span className={`inline-flex items-center text-xs font-medium px-2 py-1 rounded ${
                        isDark ? 'bg-slate-700/70 text-slate-300' : 'bg-slate-100 text-slate-700'
                      }`}>
                        <Check className="w-3 h-3 mr-1" /> Otomatik atama
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className={`hidden md:block w-16 h-16 ${isDark ? 'opacity-20' : 'opacity-10'}`}>
                  <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                    <path fill="currentColor" d="M40,86 L40,40 L86,40 L86,86 L40,86 Z M114,86 L114,40 L160,40 L160,86 L114,86 Z M40,160 L40,114 L86,114 L86,160 L40,160 Z M114,160 L114,114 L160,114 L160,160 L114,160 Z" />
                  </svg>
                </div>
              </div>
              
              {/* Subtle accent line */}
              <div className={`absolute bottom-0 left-0 right-0 h-1 opacity-30 ${
                isDark ? 'bg-gradient-to-r from-slate-700/0 via-slate-600 to-slate-700/0' : 'bg-gradient-to-r from-slate-300/0 via-slate-400 to-slate-300/0'
              }`}></div>
            </motion.div>
            
            {/* Feature 3: Team Collaboration - Enhanced */}
            <motion.div
              variants={corporateBentoVariants}
              initial="hidden"
              whileInView="visible"
              whileHover="hover"
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4, delay: 0.15 }}
              className={`col-span-12 sm:col-span-6 lg:col-span-4 row-span-1 ${bentoCardClasses("normal")}`}
            >
              <div className={`absolute -bottom-20 -right-20 w-40 h-40 rounded-full ${
                isDark ? 'bg-slate-500/20' : 'bg-slate-300/15'
              } blur-3xl group-hover:bg-slate-500/25 transition-colors duration-300`}></div>
              
              {/* Kart için desen arka planı */}
              <div className="absolute inset-0 opacity-[0.025] bg-[linear-gradient(45deg,rgba(120,120,120,0.2)_1px,transparent_1px),linear-gradient(135deg,rgba(120,120,120,0.2)_1px,transparent_1px)] bg-[size:20px_20px] group-hover:opacity-[0.05] transition-opacity duration-500"></div>
              
                {/* Animasyonlu ve soft tonlarda dekoratif elementler */}
<div className="absolute top-6 right-6 flex space-x-1.5 opacity-50">
  <motion.div 
    className="w-1.5 h-1.5 rounded-full bg-slate-400 shadow-sm shadow-slate-500/50"
    animate={{ scale: [1, 1.5, 1] }}
    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
  ></motion.div>
  <motion.div 
    className="w-1.5 h-1.5 rounded-full bg-slate-500 shadow-sm shadow-slate-600/50"
    animate={{ scale: [1, 1.5, 1] }}
    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
  ></motion.div>
  <motion.div 
    className="w-1.5 h-1.5 rounded-full bg-slate-600 shadow-sm shadow-slate-700/50"
    animate={{ scale: [1, 1.5, 1] }}
    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 1 }}
  ></motion.div>
</div>

              
              <div className="relative z-10 flex items-start space-x-4">
                <div className={`flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-xl ${
                  isDark 
                    ? 'bg-gradient-to-br from-slate-700 to-slate-800 shadow-lg shadow-slate-900/20' 
                    : 'bg-gradient-to-br from-slate-200 to-slate-300 shadow-lg shadow-slate-500/10'
                }`}>
                  <Users className={`w-5 h-5 ${isDark ? 'text-slate-300' : 'text-slate-700'}`} />
                </div>
                
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} group-hover:translate-x-1 transition-transform duration-300`}>
                      <TitleGradient>Ekip İş Birliği</TitleGradient>
                    </h3>
                    <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                      isDark ? 'bg-slate-700 text-slate-300 ring-1 ring-slate-600/40' : 'bg-slate-100 text-slate-700 ring-1 ring-slate-200'
                    }`}>
                      Yeni
                    </span>
                  </div>
                  
                  <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} max-w-sm`}>
                    Merkezi iletişim, paylaşılan dosyalar ve gerçek zamanlı güncellemeler ile ekip çalışması verimliliğini artırın.
                  </p>
                  
                  <motion.div 
                    className="mt-3 relative"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className={`flex -space-x-2`}>
                      {[...Array(4)].map((_, i) => (
                        <div 
                          key={i} 
                          className={`w-6 h-6 rounded-full border-2 ${
                            isDark ? 'border-slate-800 bg-slate-700' : 'border-white bg-slate-200'
                          } flex items-center justify-center text-[10px] font-medium overflow-hidden ${
                            isDark ? 'text-slate-300' : 'text-slate-700'
                          }`}
                        >
                          {String.fromCharCode(65 + i)}
                        </div>
                      ))}
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-[10px] font-medium ${
                        isDark 
                          ? 'border-slate-800 bg-slate-900 text-slate-300'
                          : 'border-white bg-slate-300 text-slate-700'
                      }`}>
                        +3
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
            
            {/* Feature 4: Time Tracking - Wide Card - Enhanced */}
            <motion.div
              variants={corporateBentoVariants}
              initial="hidden"
              whileInView="visible"
              whileHover="hover"
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className={`col-span-12 sm:col-span-6 lg:col-span-8 row-span-1 ${bentoCardClasses("normal")}`}
            >
              {/* Modern dot-grid pattern background */}
              <div className="absolute inset-0 opacity-[0.02] bg-[radial-gradient(circle,rgba(120,120,120,0.3)_1px,transparent_1px)] bg-[size:20px_20px] group-hover:opacity-[0.04] transition-opacity duration-500"></div>
              
              
            
              <div className={`absolute -top-20 -left-20 w-40 h-40 rounded-full ${
                isDark ? 'bg-slate-500/20' : 'bg-slate-300/15'
              } blur-3xl group-hover:bg-slate-500/25 transition-colors duration-300`}></div>
              
              <div className="relative z-10 flex items-start space-x-5">
                <div className={`flex-shrink-0 flex items-center justify-center w-14 h-14 rounded-xl ${
                  isDark 
                    ? 'bg-gradient-to-br from-slate-700 to-slate-800 shadow-lg shadow-slate-900/20' 
                    : 'bg-gradient-to-br from-slate-200 to-slate-300 shadow-lg shadow-slate-500/10'
                }`}>
                  <Clock className={`w-6 h-6 ${isDark ? 'text-slate-300' : 'text-slate-700'}`} />
                </div>
                
                <div className="flex-grow">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} group-hover:translate-x-1 transition-transform duration-300`}>
                      <TitleGradient>İş Gücü ve Zaman Yönetimi</TitleGradient>
                    </h3>
                    
                    <div className={`px-2.5 py-1 rounded-md text-xs font-medium ${
                      isDark ? 'bg-slate-700/60 text-slate-300' : 'bg-slate-100 text-slate-800'
                    }`}>
                      Enterprise Ready
                    </div>
                  </div>
                  
                  <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} max-w-2xl`}>
                    Kurumsal düzeyde zaman takibi ve iş gücü analizi ile kaynakları daha verimli kullanın. Detaylı raporlar ve analizler sayesinde proje maliyetlerini optimize edin.
                  </p>
                  
                  <div className={`mt-5 flex flex-wrap items-center gap-3`}>
                    <div className={`inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${
                      isDark ? 'bg-slate-700/70 text-slate-300 ring-1 ring-slate-600/40' : 'bg-slate-100/90 text-slate-800 ring-1 ring-slate-200'
                    }`}>
                      <div className={`w-3 h-3 rounded-full ${isDark ? 'bg-slate-500' : 'bg-slate-400'}`}></div>
                      <span>Verimlilik Analizi</span>
                    </div>
                    
                    <div className={`inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${
                      isDark ? 'bg-slate-700/70 text-slate-300 ring-1 ring-slate-600/40' : 'bg-slate-100/90 text-slate-800 ring-1 ring-slate-200'
                    }`}>
                      <div className={`w-3 h-3 rounded-full ${isDark ? 'bg-slate-500' : 'bg-slate-400'}`}></div>
                      <span>Otomatik Raporlama</span>
                    </div>
                    
                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.98 }}
                      className={`ml-auto flex items-center px-3 py-1.5 rounded-lg text-xs font-medium ${
                        isDark 
                          ? 'bg-slate-800 text-slate-300 hover:bg-slate-700 ring-1 ring-slate-700' 
                          : 'bg-slate-200 text-slate-800 hover:bg-slate-300 ring-1 ring-slate-300'
                      }`}
                    >
                      <span>Demo Görüntüle</span>
                      <ArrowRight className="w-3 h-3 ml-1.5" />
                    </motion.button>
                  </div>
                </div>
              </div>
              
              {/* Subtle accent line */}
              <div className={`absolute bottom-0 left-0 right-0 h-0.5 opacity-30 ${
                isDark ? 'bg-gradient-to-r from-slate-700/0 via-slate-500 to-slate-700/0' : 'bg-gradient-to-r from-slate-300/0 via-slate-400 to-slate-300/0'
              }`}></div>
            </motion.div>
            
            {/* Feature 5: Security - Column Card - Enhanced */}
            <motion.div
              variants={corporateBentoVariants}
              initial="hidden"
              whileInView="visible"
              whileHover="hover"
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4, delay: 0.25 }}
              className={`col-span-12 sm:col-span-6 lg:col-span-4 row-span-2 ${bentoCardClasses("tall")}`}
            >
              {/* Hexagonal pattern background */}
              <div className="absolute inset-0 opacity-[0.02] bg-[repeating-linear-gradient(45deg,rgba(120,120,120,0.1)_0px,rgba(120,120,120,0.1)_1px,transparent_1px,transparent_10px)] group-hover:opacity-[0.04] transition-opacity duration-500"></div>
              
{/* Animasyonlu ve soft tonlarda dekoratif elementler */}
<div className="absolute top-6 right-6 flex space-x-1.5 opacity-50">
<motion.div 
    className="w-1.5 h-1.5 rounded-full bg-slate-400 shadow-sm shadow-slate-500/50"
    animate={{ scale: [1, 1.5, 1] }}
    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
  ></motion.div>
<motion.div 
    className="w-1.5 h-1.5 rounded-full bg-slate-500 shadow-sm shadow-slate-600/50"
    animate={{ scale: [1, 1.5, 1] }}
    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
  ></motion.div>
                <motion.div 
                  className="w-1.5 h-1.5 rounded-full bg-slate-600 shadow-sm shadow-slate-700/50"
                  animate={{ scale: [1, 1.5, 1] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 1 }}
  ></motion.div>
</div>

              <div className={`absolute -bottom-20 -right-20 w-40 h-40 rounded-full ${
                isDark ? 'bg-slate-500/20' : 'bg-slate-300/15'
              } blur-3xl group-hover:bg-slate-500/25 transition-colors duration-300`}></div>
              
              <div className="relative z-10 flex flex-col h-full mt-6">
                <div className="flex items-start justify-between mb-6">
                  <div className={`flex items-center justify-center w-14 h-14 rounded-2xl ${
                  isDark 
                    ? 'bg-gradient-to-br from-slate-700 to-slate-800 shadow-lg shadow-slate-900/20' 
                    : 'bg-gradient-to-br from-slate-200 to-slate-300 shadow-lg shadow-slate-500/10'
                }`}>
                    <Shield className={`w-6 h-6 ${isDark ? 'text-slate-300' : 'text-slate-700'}`} />
                </div>
                
                  <div className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                    isDark ? 'bg-slate-700/70 text-slate-300 ring-1 ring-slate-600/40' : 'bg-slate-100 text-slate-700 ring-1 ring-slate-200/70'
                  }`}>
                    SOC 2 Uyumlu
                  </div>
                </div>
                
                <h3 className={`text-2xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'} group-hover:translate-x-1 transition-transform duration-300`}>
                  <TitleGradient>Kurumsal Güvenlik</TitleGradient>
                </h3>
                
                <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-6 leading-relaxed`}>
                  Banka düzeyinde şifreleme, gelişmiş erişim kontrolleri ve kapsamlı denetim kayıtları ile verilerinizi koruyoruz.
                </p>
                
                <div className="mt-auto space-y-4">
                  <div className={`p-4 rounded-xl ${
                    isDark ? 'bg-slate-800/70 ring-1 ring-slate-700/80' : 'bg-slate-50/90 ring-1 ring-slate-200/70'
                  } backdrop-blur-sm`}>
                    <h4 className={`text-sm font-semibold mb-3 ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                      Güvenlik Sertifikaları
                  </h4>
                    <div className="space-y-2">
                      {[
                        "ISO 27001 Sertifikalı", 
                        "GDPR Uyumlu",
                        "SSL/TLS Şifreleme", 
                        "2FA Kimlik Doğrulama"
                      ].map((cert, i) => (
                      <div key={i} className="flex items-center space-x-2">
                          <Check className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-slate-600'}`} />
                        <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {cert}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                
                  <div className={`p-3 rounded-lg ${
                    isDark ? 'bg-slate-700/50' : 'bg-slate-100/70'
                  }`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                        Sistem Güvenilirliği
                  </span>
                      <span className={`text-xs font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
  <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-green-600 font-bold">
    99.9%
  </span>{' '}
  Uptime
</span>

                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* Feature 5b: Corporate Slogan Card - New wide card */}
            <motion.div
              variants={corporateBentoVariants}
              initial="hidden"
              whileInView="visible"
              whileHover="hover"
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4, delay: 0.25 }}
              className="col-span-12 sm:col-span-6 lg:col-span-8 row-span-2 rounded-3xl overflow-hidden transition-all duration-300 relative min-h-[450px]"
            >
              {/* Enhanced gradient background */}
              <div className={`absolute inset-0 ${
                isDark 
                  ? 'bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800' 
                  : 'bg-gradient-to-br from-slate-100 via-slate-50 to-slate-100'
              }`}></div>
              
              {/* Background pattern with icons */}
              <div className="absolute inset-0 overflow-hidden opacity-[0.03]">
                {/* Security pattern icons in grid */}
                <div className="absolute inset-0 grid grid-cols-8 grid-rows-5 gap-8">
                  {[Shield, Lock, Users, Clock, BarChart2, Check, MessageCircle, Layout].map((Icon, index) => (
                    [...Array(6)].map((_, i) => (
                      <Icon 
                        key={`${index}-${i}`} 
                        className={`w-6 h-6 text-slate-500 transform rotate-${Math.floor(Math.random() * 4) * 90}`}
                        style={{
                          position: 'absolute',
                          top: `${20 + Math.random() * 60}%`,
                          left: `${10 + Math.random() * 80}%`,
                          opacity: 0.1 + (Math.random() * 0.1),
                        }}
                      />
                    ))
                  ))}
                </div>
              </div>
              
              {/* Enhanced decorative elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-slate-400/15 to-slate-400/0 rounded-bl-[120px]"></div>
              <div className={`absolute bottom-0 left-0 w-80 h-80 rounded-tr-[200px] ${isDark ? 'bg-slate-700/10' : 'bg-slate-300/10'}`}></div>
              
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full border border-slate-500/10 opacity-50"></div>
                <div className="absolute -bottom-20 -left-20 w-40 h-40 rounded-full border border-slate-500/10 opacity-30"></div>
                <div className={`absolute top-20 left-24 w-40 h-40 rounded-full ${isDark ? 'bg-slate-600/5' : 'bg-slate-400/5'} blur-xl`}></div>
                <div className={`absolute bottom-20 right-24 w-60 h-60 rounded-full ${isDark ? 'bg-slate-600/5' : 'bg-slate-400/5'} blur-xl`}></div>
              </div>
              
              <div className="relative z-10 p-8 h-full flex flex-col justify-between">
                {/* Large accent graphic element */}
                <div className="absolute top-0 right-0 w-32 h-64 opacity-20">
                  <div className="absolute top-8 right-8 w-16 h-16 border-8 border-slate-500/20 rounded-full"></div>
                  <div className="absolute top-16 right-4 w-3 h-24 bg-gradient-to-b from-slate-500/30 to-transparent rounded-full"></div>
                </div>
                
                {/* Main content */}
                <div>
                  {/* Badge */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium mb-8 ${
                      isDark ? 'bg-slate-700/70 text-slate-300 ring-1 ring-slate-600/40' : 'bg-slate-100 text-slate-700 ring-1 ring-slate-200'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full mr-2 ${isDark ? 'bg-green-400' : 'bg-green-500'}`}></span>
                    Kurumsal Çözüm
                  </motion.div>
                  
                  <h2 className={`text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold tracking-tight mb-6 leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Verimlilik ve <span className="relative inline-block">
                      <span className="relative z-10">
                        {isDark ? (
                          <span className="bg-clip-text text-transparent bg-gradient-to-r from-gray-100 via-white to-gray-200">
                            Güvenlik
                          </span>
                        ) : (
                          <span className="bg-clip-text text-transparent bg-gradient-to-r from-slate-700 via-slate-900 to-slate-800">
                            Güvenlik
                          </span>
                        )}
                      </span>
                      <span className={`absolute -bottom-1 left-0 right-0 h-2 ${
                        isDark ? 'bg-gradient-to-r from-red-500/40 via-red-600/40 to-red-500/40' : 'bg-gradient-to-r from-red-500/20 via-red-600/30 to-red-500/20'
                      } rounded-sm`}></span>
                    </span> bir arada.
                  </h2>
                  
                  <p className={`text-lg md:text-xl mb-8 max-w-2xl ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  } leading-relaxed`}>
                    Kurumsal düzeyde güvenlik altyapımız ve ileri teknolojimizle işletmenizi güvenle dijital dünyaya taşıyın.
                    Verileriniz <span className="font-semibold">%100 güvende</span>, süreçleriniz <span className="font-semibold">tamamen optimize</span>.
                  </p>
                  
                  {/* Added feature list for more details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                    {[
                      { icon: Shield, title: "Uçtan Uca Şifreleme", desc: "Verileriniz her aşamada şifreli ve güvende" },
                      { icon: Zap, title: "Hızlı Performans", desc: "Optimize edilmiş altyapı ile kesintisiz çalışma" },
                      { icon: Users, title: "Ekip Yönetimi", desc: "Detaylı yetkilendirme ve rol bazlı erişim" },
                      { icon: BarChart2, title: "İş Analitiği", desc: "Gerçek zamanlı veri ve performans izleme" }
                    ].map((feature, idx) => (
                      <motion.div 
                        key={idx}
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 + (idx * 0.1) }}
                        className="flex items-start space-x-3"
                      >
                        <div className={`flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-lg ${
                          isDark 
                            ? 'bg-slate-700/80 text-slate-300' 
                            : 'bg-slate-200/80 text-slate-700'
                        }`}>
                          <feature.icon className="w-4 h-4" />
                        </div>
                        <div>
                          <h4 className={`text-base font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {feature.title}
                          </h4>
                          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            {feature.desc}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
                
                {/* Bottom section with certification */}
                <div>
                  {/* Certification badge */}
                  <div className={`p-4 rounded-xl ${
                    isDark ? 'bg-slate-800/80 ring-1 ring-slate-700' : 'bg-white/70 ring-1 ring-slate-200/80 shadow-sm'
                  } backdrop-blur-sm`}>
                    <div className="flex items-center">
                      <div className={`flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-lg mr-4 ${
                        isDark ? 'bg-slate-700' : 'bg-slate-100'
                      }`}>
                        <Shield className={`w-6 h-6 ${isDark ? 'text-slate-300' : 'text-slate-700'}`} />
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className={`flex items-center space-x-1.5`}>
                            <div className={`h-2 w-12 rounded-full ${isDark ? 'bg-slate-600' : 'bg-slate-300'}`}></div>
                            <div className={`h-2 w-4 rounded-full ${isDark ? 'bg-slate-500' : 'bg-slate-200'}`}></div>
                          </span>
                          <span className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-800'}`}>
                            ISO 27001 sertifikalı güvenlik altyapısı
                          </span>
                        </div>
                        <div className={`mt-1 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                          KVKK uyumlu veri koruma politikaları ve düzenli güvenlik denetimleri
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* Feature 6: Custom Dashboards */}
            <motion.div
              variants={corporateBentoVariants}
              initial="hidden"
              whileInView="visible"
              whileHover="hover"
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4, delay: 0.3 }}
              className={`col-span-12 sm:col-span-6 lg:col-span-12 row-span-1 ${bentoCardClasses("normal")}`}
            >
              <div className={`absolute -top-20 -right-20 w-40 h-40 rounded-full ${
                isDark ? 'bg-slate-500/20' : 'bg-slate-300/15'
              } blur-3xl group-hover:bg-slate-500/25 transition-colors duration-300`}></div>
              
              <div className="relative z-10 flex items-start space-x-5">
                <div className={`flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-xl ${
                  isDark 
                    ? 'bg-gradient-to-br from-slate-700 to-slate-800 shadow-lg shadow-slate-900/20' 
                    : 'bg-gradient-to-br from-slate-200 to-slate-300 shadow-lg shadow-slate-500/10'
                }`}>
                  <PanelRight className={`w-5 h-5 ${isDark ? 'text-slate-300' : 'text-slate-700'}`} />
                </div>
                
                <div className="flex-grow">
                  <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'} group-hover:translate-x-1 transition-transform duration-300`}>
                    Özelleştirilebilir Paneller
                  </h3>
                  
                  <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    İş akışınızı hızlandıran, tamamen kişiselleştirilebilir kontrol panelleri. Widget'ları sürükleyip bırakarak ideal çalışma ortamınızı oluşturun, veri görselleştirmelerini özelleştirin ve kritik bilgilere anında erişin.
                  </p>
                  
                  <div className={`mt-4 flex flex-wrap gap-2`}>
                    <div className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                      isDark ? 'bg-slate-700/70 text-slate-300' : 'bg-slate-100/70 text-slate-800'
                    }`}>
                      Sürükle-Bırak Arayüz
                    </div>
                    <div className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                      isDark ? 'bg-slate-700/70 text-slate-300' : 'bg-slate-100/70 text-slate-800'
                    }`}>
                      Kişiselleştirilebilir Görünüm
                    </div>
                    <div className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                      isDark ? 'bg-slate-700/70 text-slate-300' : 'bg-slate-100/70 text-slate-800'
                    }`}>
                      Özel Veri Göstergeleri
                    </div>
                    <div className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                      isDark ? 'bg-slate-700/70 text-slate-300' : 'bg-slate-100/70 text-slate-800'
                    }`}>
                      Rol Bazlı Görünümler
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* Feature 7: Messaging - Square Medium Card */}
            <motion.div
              variants={corporateBentoVariants}
              initial="hidden"
              whileInView="visible"
              whileHover="hover"
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4, delay: 0.35 }}
              className={`col-span-12 sm:col-span-6 lg:col-span-7 row-span-1 ${bentoCardClasses("normal")}`}
            >
              <div className={`absolute -bottom-20 -left-20 w-40 h-40 rounded-full ${
                isDark ? 'bg-slate-500/20' : 'bg-slate-300/15'
              } blur-3xl group-hover:bg-slate-500/25 transition-colors duration-300`}></div>
              
              <div className="relative z-10 flex flex-col h-full">
                <div className={`flex items-center justify-center w-12 h-12 rounded-xl mb-4 ${
                  isDark 
                    ? 'bg-gradient-to-br from-slate-700 to-slate-800 shadow-lg shadow-slate-900/20' 
                    : 'bg-gradient-to-br from-slate-200 to-slate-300 shadow-lg shadow-slate-500/10'
                }`}>
                  <MessageCircle className={`w-5 h-5 ${isDark ? 'text-slate-300' : 'text-slate-700'}`} />
                </div>
                
                <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'} group-hover:translate-x-1 transition-transform duration-300`}>
                  Takım Mesajlaşması
                </h3>
                
                <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} flex-grow`}>
                  Ekip içi iletişimi güçlendiren entegre mesajlaşma platformu. Proje tabanlı grup sohbetleri, dosya paylaşımı, arama geçmişi ve özel thread'ler ile ekip çalışmasını daha verimli hale getirin. Tüm mesajlar otomatik olarak ilgili projelerle ilişkilendirilir.
                </p>
                
                <div className={`mt-4 flex flex-wrap gap-2`}>
                  <div className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                    isDark ? 'bg-slate-700/70 text-slate-300' : 'bg-slate-100/70 text-slate-800'
                  }`}>
                    Gerçek Zamanlı İletişim
                  </div>
                  <div className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                    isDark ? 'bg-slate-700/70 text-slate-300' : 'bg-slate-100/70 text-slate-800'
                  }`}>
                    Proje Grupları
                  </div>
                  <div className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                    isDark ? 'bg-slate-700/70 text-slate-300' : 'bg-slate-100/70 text-slate-800'
                  }`}>
                    Dosya Paylaşımı
                  </div>
                  <div className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                    isDark ? 'bg-slate-700/70 text-slate-300' : 'bg-slate-100/70 text-slate-800'
                  }`}>
                    Okundu Bildirimleri
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* Feature 8: Integration - Square Medium Card */}
            <motion.div
              variants={corporateBentoVariants}
              initial="hidden"
              whileInView="visible"
              whileHover="hover"
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4, delay: 0.4 }}
              className={`col-span-12 sm:col-span-6 lg:col-span-5 row-span-1 ${bentoCardClasses("normal")} flex flex-col`}
            >
              <div className={`absolute -top-20 -right-20 w-40 h-40 rounded-full ${
                isDark ? 'bg-slate-500/20' : 'bg-slate-300/15'
              } blur-3xl group-hover:bg-slate-500/25 transition-colors duration-300`}></div>
              
              <div className="relative z-10 h-full flex flex-col">
                <div className={`flex items-center justify-center w-12 h-12 rounded-xl mb-4 ${
                  isDark 
                    ? 'bg-gradient-to-br from-slate-700 to-slate-800 shadow-lg shadow-slate-900/20' 
                    : 'bg-gradient-to-br from-slate-200 to-slate-300 shadow-lg shadow-slate-500/10'
                }`}>
                  <Layout className={`w-5 h-5 ${isDark ? 'text-slate-300' : 'text-slate-700'}`} />
                </div>
                
                <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'} group-hover:translate-x-1 transition-transform duration-300`}>
                  API Entegrasyonları
                </h3>
                
                <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} flex-grow`}>
                  Tüm iş araçlarınızı Beetrax platformuna entegre edin. REST API, webhook'lar ve özel bağlayıcılar ile popüler CRM, muhasebe yazılımları, bulut depolama ve analitik araçlarıyla sorunsuz entegrasyon sağlayın. Kapsamlı dokümantasyon ve kullanıma hazır şablonlar ile hızlıca bağlantı kurun.
                </p>
                
                <div className="mt-4 flex flex-wrap gap-2">
                  <div className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                    isDark ? 'bg-slate-700/70 text-slate-300' : 'bg-slate-100/70 text-slate-800'
                  }`}>
                    REST API
                  </div>
                  <div className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                    isDark ? 'bg-slate-700/70 text-slate-300' : 'bg-slate-100/70 text-slate-800'
                  }`}>
                    Webhook Desteği
                  </div>
                  <div className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                    isDark ? 'bg-slate-700/70 text-slate-300' : 'bg-slate-100/70 text-slate-800'
                  }`}>
                    CRM Entegrasyonu
                  </div>
                </div>
                
                <motion.div 
                  className={`mt-4 w-10 h-10 rounded-full flex items-center justify-center ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}
                  whileHover={{ rotate: 180 }}
                  transition={{ duration: 0.5 }}
                >
                  <Zap className={`w-4 h-4 ${isDark ? 'text-slate-300' : 'text-slate-700'}`} />
                </motion.div>
              </div>
            </motion.div>

            {/* Feature 6: Stock Management - New Feature - Wide Card */}
            <motion.div
              variants={corporateBentoVariants}
              initial="hidden"
              whileInView="visible"
              whileHover="hover"
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4, delay: 0.3 }}
              className={`col-span-12 row-span-1 ${bentoCardClasses("normal")}`}
            >
              {/* Modern geometric pattern background */}
              <div className="absolute inset-0 opacity-[0.02] bg-[linear-gradient(30deg,rgba(120,120,120,0.1)_12%,transparent_12.5%,transparent_87%,rgba(120,120,120,0.1)_87.5%,rgba(120,120,120,0.1)),linear-gradient(150deg,rgba(120,120,120,0.1)_12%,transparent_12.5%,transparent_87%,rgba(120,120,120,0.1)_87.5%,rgba(120,120,120,0.1)),linear-gradient(30deg,rgba(120,120,120,0.1)_12%,transparent_12.5%,transparent_87%,rgba(120,120,120,0.1)_87.5%,rgba(120,120,120,0.1)),linear-gradient(150deg,rgba(120,120,120,0.1)_12%,transparent_12.5%,transparent_87%,rgba(120,120,120,0.1)_87.5%,rgba(120,120,120,0.1))] bg-[size:80px_140px] group-hover:opacity-[0.04] transition-opacity duration-500"></div>
              
              <div className={`absolute -top-20 -right-20 w-40 h-40 rounded-full ${
                isDark ? 'bg-slate-500/20' : 'bg-slate-300/15'
              } blur-3xl group-hover:bg-slate-500/25 transition-colors duration-300`}></div>
              
              <div className="relative z-10 flex items-start space-x-5">
                <div className={`flex-shrink-0 flex items-center justify-center w-14 h-14 rounded-xl ${
                  isDark 
                    ? 'bg-gradient-to-br from-slate-700 to-slate-800 shadow-lg shadow-slate-900/20' 
                    : 'bg-gradient-to-br from-slate-200 to-slate-300 shadow-lg shadow-slate-500/10'
                }`}>
                  <Package className={`w-6 h-6 ${isDark ? 'text-slate-300' : 'text-slate-700'}`} />
                </div>
                
                <div className="flex-grow">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} group-hover:translate-x-1 transition-transform duration-300`}>
                      <TitleGradient>Akıllı Stok Yönetimi Sistemi</TitleGradient>
                    </h3>
                    
                    <div className={`px-2.5 py-1 rounded-md text-xs font-medium ${
                      isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800'
                    }`}>
                      Yeni Özellik
                    </div>
                  </div>
                  
                  <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} max-w-3xl mb-4`}>
                    Gelişmiş envanter takibi, otomatik stok uyarıları ve tedarik zinciri yönetimi ile stok süreçlerinizi optimize edin. 
                    Barcode/QR kod okuyucu, lot takibi ve son kullanma tarihi yönetimi ile tam kontrol sağlayın.
                  </p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    {[
                      { icon: <BarChart2 className="w-4 h-4" />, label: "Envanter Analizi" },
                      { icon: <AlertTriangle className="w-4 h-4" />, label: "Stok Uyarıları" },
                      { icon: <Truck className="w-4 h-4" />, label: "Tedarik Yönetimi" },
                      { icon: <QrCode className="w-4 h-4" />, label: "QR/Barcode" }
                    ].map((feature, i) => (
                      <div key={i} className={`flex items-center space-x-2 px-3 py-2 rounded-lg ${
                        isDark ? 'bg-slate-700/50 text-slate-300' : 'bg-slate-100/70 text-slate-700'
                      }`}>
                        {feature.icon}
                        <span className="text-xs font-medium">{feature.label}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className={`flex flex-wrap items-center gap-3`}>
                    <div className={`inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${
                      isDark ? 'bg-slate-700/70 text-slate-300 ring-1 ring-slate-600/40' : 'bg-slate-100/90 text-slate-800 ring-1 ring-slate-200'
                    }`}>
                      <div className={`w-3 h-3 rounded-full ${isDark ? 'bg-green-500' : 'bg-green-400'}`}></div>
                      <span>Gerçek Zamanlı Takip</span>
                    </div>
                    
                    <div className={`inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${
                      isDark ? 'bg-slate-700/70 text-slate-300 ring-1 ring-slate-600/40' : 'bg-slate-100/90 text-slate-800 ring-1 ring-slate-200'
                    }`}>
                      <div className={`w-3 h-3 rounded-full ${isDark ? 'bg-blue-500' : 'bg-blue-400'}`}></div>
                      <span>Otomatik Siparişler</span>
                    </div>
                    
                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.98 }}
                      className={`ml-auto flex items-center px-3 py-1.5 rounded-lg text-xs font-medium ${
                        isDark 
                          ? 'bg-slate-800 text-slate-300 hover:bg-slate-700 ring-1 ring-slate-700' 
                          : 'bg-slate-200 text-slate-800 hover:bg-slate-300 ring-1 ring-slate-300'
                      }`}
                    >
                      <span>Özellik Detayları</span>
                      <ArrowRight className="w-3 h-3 ml-1.5" />
                    </motion.button>
                  </div>
                </div>
              </div>
              
              {/* Accent line */}
              <div className={`absolute bottom-0 left-0 right-0 h-0.5 opacity-30 ${
                isDark ? 'bg-gradient-to-r from-slate-700/0 via-slate-500 to-slate-700/0' : 'bg-gradient-to-r from-slate-300/0 via-slate-400 to-slate-300/0'
              }`}></div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Pricing Section */}
      <section id="pricing" className="py-20 md:py-28 relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">Şeffaf</span> Fiyatlandırma
            </h2>
            <p className={`text-lg ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              İşletmenizin büyüklüğüne ve ihtiyaçlarınıza uygun esnek planlar oluşturun.
            </p>
          </div>
          
          {/* Pricing Calculator */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className={`max-w-4xl mx-auto mb-16 rounded-3xl overflow-hidden ${
              isDark 
                ? 'bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-xl border border-slate-700/50 shadow-2xl shadow-slate-900/30' 
                : 'bg-gradient-to-br from-white/95 to-slate-100/95 backdrop-blur-xl border border-slate-200/70 shadow-2xl shadow-slate-200/40'
            }`}
          >
            {/* Dekoratif arka plan öğeleri */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className={`absolute -top-40 -right-40 w-80 h-80 rounded-full ${
                isDark ? 'bg-blue-600/5' : 'bg-blue-500/5'
              } blur-3xl`}></div>
              <div className={`absolute -bottom-40 -left-40 w-80 h-80 rounded-full ${
                isDark ? 'bg-purple-600/5' : 'bg-purple-500/5'
              } blur-3xl`}></div>
              
              {/* Noktalı desen arka plan */}
              <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle,rgba(120,120,120,0.1)_1px,transparent_1px)] bg-[size:16px_16px]"></div>
                  </div>
            
            {/* Başlık kısmı - ayrı bir bölüm olarak */}
            <div className={`relative p-6 ${
              isDark ? 'bg-slate-800/50 border-b border-slate-700/50' : 'bg-white/50 border-b border-slate-200/50'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className={`text-xl md:text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Fiyat Simülasyonu
                    </h3>
                  <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    İhtiyacınıza en uygun planı oluşturun
                  </p>
                </div>
                
                <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium ${
                  isDark ? 'bg-blue-900/30 text-blue-300 ring-1 ring-blue-700/30' : 'bg-blue-50 text-blue-700 ring-1 ring-blue-200/70'
                }`}>
                  <span className="relative flex h-2 w-2 mr-2">
                    <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${isDark ? 'bg-blue-400' : 'bg-blue-400'} opacity-75`}></span>
                    <span className={`relative inline-flex rounded-full h-2 w-2 ${isDark ? 'bg-blue-500' : 'bg-blue-500'}`}></span>
                      </span>
                  Canlı Hesaplama
                    </div>
              </div>
                  </div>
                  
            <div className="p-8 relative">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Left Column - Options */}
                <div className="space-y-8">
                  {/* Product Selection */}
                  <div className="space-y-4">
                    <label className={`block text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'} flex items-center`}>
                      <span className={`inline-block mr-2 p-1 rounded ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}>
                        <Package className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                        </span>
                      Ürün Seçimi
                    </label>
                    <div className="grid grid-cols-1 gap-3">
                      <ProductOption
                        id="task-management"
                        name="İş Takip Sistemi"
                        description="Projelerinizi ve görevlerinizi yönetin"
                        icon={<Calendar className="w-5 h-5" />}
                        price={productData['task-management'].basePrice}
                        isDark={isDark}
                      />
                      
                      <ProductOption
                        id="stock-management"
                        name="Stok Yönetimi Sistemi"
                        description="Envanterinizi takip edin"
                        icon={<Package className="w-5 h-5" />}
                        price={productData['stock-management'].basePrice}
                        isDark={isDark}
                      />
                      
                      <ProductOption
                        id="complete-suite"
                        name="Tam Paket (İkisi Bir Arada)"
                        description="İş ve stok yönetimini birleştirin"
                        icon={<Zap className="w-5 h-5" />}
                        price={productData['complete-suite'].basePrice}
                        isDark={isDark}
                        recommended={true}
                      />
                    </div>
                  </div>
                  
                  {/* Billing Period */}
                  <div className="space-y-4">
                    <label className={`block text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'} flex items-center`}>
                      <span className={`inline-block mr-2 p-1 rounded ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}>
                        <Clock className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                      </span>
                      Ödeme Periyodu
                    </label>
                    <div className={`p-1.5 inline-flex rounded-xl w-full ${
                      isDark ? 'bg-slate-700/70' : 'bg-slate-100/80'
                    }`}>
                  <button
                        onClick={() => handleBillingPeriodChange('monthly')}
                        className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                          billingPeriod === 'monthly'
                            ? (isDark 
                                ? 'bg-gradient-to-r from-blue-700 to-blue-600 text-white shadow-lg shadow-blue-800/20' 
                                : 'bg-white text-slate-800 shadow-md')
                            : (isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-800')
                        }`}
                      >
                        Aylık Ödeme
                  </button>
                      <button
                        onClick={() => handleBillingPeriodChange('yearly')}
                        className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                          billingPeriod === 'yearly'
                            ? (isDark 
                                ? 'bg-gradient-to-r from-blue-700 to-blue-600 text-white shadow-lg shadow-blue-800/20' 
                                : 'bg-white text-slate-800 shadow-md')
                            : (isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-800')
                        }`}
                      >
                        <div className="flex items-center justify-center space-x-1">
                          <span>Yıllık Ödeme</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                            isDark ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700'
                          }`}>%16 İndirim</span>
                </div>
                      </button>
                    </div>
                  </div>
                  
                  {/* User Count */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <label className={`block text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'} flex items-center`}>
                        <span className={`inline-block mr-2 p-1 rounded ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}>
                          <Users className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                        </span>
                        Kullanıcı Sayısı
                      </label>
                      <motion.div 
                        initial={{ scale: 1 }}
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 0.3 }}
                        key={userCount}
                        className={`px-3 py-1 rounded-full text-lg font-semibold ${
                          isDark 
                            ? 'bg-slate-700 text-white border border-slate-600' 
                            : 'bg-white text-slate-800 border border-slate-200 shadow-sm'
                        }`}
                      >
                        {userCount}
              </motion.div>
                    </div>
                    
                    <div className="relative">
                      {/* Range background bar */}
                      <div className={`absolute inset-0 h-2 top-1/2 -translate-y-1/2 rounded-full ${
                        isDark ? 'bg-slate-700/70' : 'bg-slate-200/70'
                      }`}></div>
                      
                      {/* Range custom styling */}
                      <input 
                        type="range" 
                        min="1" 
                        max="50" 
                        value={userCount}
                        onChange={handleUserCountChange}
                        className={`w-full h-6 rounded-lg appearance-none bg-transparent cursor-pointer relative z-10 ${
                          isDark ? '[&::-webkit-slider-thumb]:bg-blue-500' : '[&::-webkit-slider-thumb]:bg-blue-600'
                        } [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:relative [&::-webkit-slider-thumb]:z-20`}
                      />
                      
                      {/* Range completed part */}
                      <div 
                        className={`absolute h-2 top-1/2 -translate-y-1/2 left-0 rounded-full ${
                          isDark ? 'bg-gradient-to-r from-blue-700 to-blue-500' : 'bg-gradient-to-r from-blue-600 to-blue-400'
                        }`}
                        style={{ width: `${(userCount / 50) * 100}%` }}
                      ></div>
                    </div>
                    
                    <div className="flex justify-between text-xs">
                      <div className="flex flex-col items-center">
                        <span className={`px-2 py-0.5 rounded ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>1</span>
                        <span className={`mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Minimum</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <span className={`px-2 py-0.5 rounded ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>10</span>
                        <span className={`mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Küçük Ekip</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <span className={`px-2 py-0.5 rounded ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>25</span>
                        <span className={`mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Orta Ölçekli</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <span className={`px-2 py-0.5 rounded ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>50+</span>
                        <span className={`mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Kurumsal</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Right Column - Price Summary */}
                <div>
                  <div className={`p-6 rounded-2xl ${
                    isDark 
                      ? 'bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700/70 shadow-lg' 
                      : 'bg-gradient-to-br from-white to-slate-50 border border-slate-200/70 shadow-lg'
                  }`}>
                    <div className="flex items-center justify-between mb-6">
                      <h4 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Fiyat Özeti
                      </h4>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-700'
                      }`}>
                        TRY (₺)
                      </span>
                    </div>
                    
                    <div className="space-y-4">
                      <div className={`flex justify-between items-center p-3 rounded-lg ${
                        isDark ? 'bg-slate-700/50' : 'bg-slate-100/70'
                      }`}>
                        <div className="flex items-center">
                          <div className={`p-2 rounded-lg mr-3 ${
                            isDark ? 'bg-slate-600' : 'bg-white'
                          }`}>
                            {selectedProduct === 'task-management' && <Calendar className="w-5 h-5" />}
                            {selectedProduct === 'stock-management' && <Package className="w-5 h-5" />}
                            {selectedProduct === 'complete-suite' && <Zap className="w-5 h-5" />}
                          </div>
                          <span className={isDark ? 'text-slate-200' : 'text-slate-700'}>Seçilen ürün</span>
                        </div>
                        <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{productData[selectedProduct].name}</span>
                      </div>
                      
                      <div className={`flex justify-between items-center p-3 rounded-lg ${
                        isDark ? 'bg-slate-700/50' : 'bg-slate-100/70'
                      }`}>
                        <div className="flex items-center">
                          <div className={`p-2 rounded-lg mr-3 ${
                            isDark ? 'bg-slate-600' : 'bg-white'
                          }`}>
                            <Clock className="w-5 h-5" />
                          </div>
                          <span className={isDark ? 'text-slate-200' : 'text-slate-700'}>Ödeme periyodu</span>
                        </div>
                        <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {billingPeriod === 'monthly' ? 'Aylık' : 'Yıllık'}
                        </span>
                      </div>
                      
                      <div className={`flex justify-between items-center p-3 rounded-lg ${
                        isDark ? 'bg-slate-700/50' : 'bg-slate-100/70'
                      }`}>
                        <div className="flex items-center">
                          <div className={`p-2 rounded-lg mr-3 ${
                            isDark ? 'bg-slate-600' : 'bg-white'
                          }`}>
                            <Users className="w-5 h-5" />
                          </div>
                          <span className={isDark ? 'text-slate-200' : 'text-slate-700'}>Kullanıcı sayısı</span>
                        </div>
                        <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{userCount} kullanıcı</span>
                      </div>
                      
                      <div className={`flex flex-col mt-6 p-4 rounded-xl ${
                        isDark 
                          ? 'bg-slate-800 border border-slate-700' 
                          : 'bg-white border border-slate-200 shadow-sm'
                      }`}>
                        <div className="flex justify-between items-center mb-2">
                          <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Temel fiyat</span>
                          <span className={`${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                            ₺{productData[selectedProduct].basePrice.toLocaleString('tr-TR')}
                          </span>
                        </div>
                        
                        <div className="flex justify-between items-center mb-2">
                          <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Ek kullanıcılar</span>
                          <span className={`${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                            +₺{(Math.max(0, userCount - 3) * productData[selectedProduct].userPrice).toLocaleString('tr-TR')}
                          </span>
                        </div>
                        
                        {billingPeriod === 'yearly' && (
                          <div className="flex justify-between items-center mb-2">
                            <span className={`text-sm ${isDark ? 'text-green-400' : 'text-green-600'}`}>Yıllık plan indirimi</span>
                            <span className={`${isDark ? 'text-green-400' : 'text-green-600'}`}>
                              -₺{(priceDetails.totalPrice * 0.1667).toLocaleString('tr-TR')}
                            </span>
                          </div>
                        )}
                        
                        <div className={`flex justify-between items-center pt-2 mt-2 border-t ${
                          isDark ? 'border-slate-700' : 'border-slate-200'
                        }`}>
                          <span className={`text-lg font-medium ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>Toplam</span>
                          <div className="text-right">
                            <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              ₺{priceDetails.totalPrice.toLocaleString('tr-TR')}
                              <span className="text-sm font-normal ml-1">/{priceDetails.period}</span>
                            </div>
                            <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                              Kullanıcı başına ₺{Math.round(priceDetails.perUserPrice).toLocaleString('tr-TR')}/{billingPeriod === 'monthly' ? 'ay' : 'yıl'}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Yearly savings note */}
                      {billingPeriod === 'monthly' && (
                        <div className={`mt-4 p-3 rounded-lg text-sm ${
                          isDark ? 'bg-blue-900/20 text-blue-200 border border-blue-900/30' : 'bg-blue-50 text-blue-700 border border-blue-100'
                        }`}>
                          <div className="flex items-start">
                            <Lightbulb className="w-5 h-5 mr-2 flex-shrink-0" />
                            <span>
                              Yıllık ödeme planına geçerek <strong>2 ay ücretsiz</strong> kullanım ve <strong>%16 tasarruf</strong> sağlayabilirsiniz.
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {billingPeriod === 'yearly' && (
                        <div className={`mt-4 p-3 rounded-lg text-sm ${
                          isDark ? 'bg-green-900/20 text-green-200 border border-green-900/30' : 'bg-green-50 text-green-700 border border-green-100'
                        }`}>
                          <div className="flex items-start">
                            <Check className="w-5 h-5 mr-2 flex-shrink-0" />
                            <span>
                              Yıllık plan seçerek <strong>₺{Math.round(priceDetails.totalPrice * 0.1667).toLocaleString('tr-TR')}</strong> tasarruf ediyorsunuz!
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => navigate('/register?plan=' + selectedProduct + '&period=' + billingPeriod + '&users=' + userCount)}
                      className="w-full py-4 px-6 mt-6 rounded-xl font-medium text-white transition-all
                        bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600 hover:from-blue-700 hover:via-blue-600 hover:to-blue-700 shadow-lg shadow-blue-500/20
                        relative overflow-hidden group
                      "
                    >
                      {/* Light effect */}
                      <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-all duration-1000"></div>
                      
                      <div className="flex items-center justify-center relative z-10">
                        <span className="text-lg">Planı Seçin</span>
                        <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                      </div>
                    </motion.button>
                  </div>
                  
                  <div className={`mt-6 flex items-center justify-center ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    <Lock className="w-4 h-4 mr-2" />
                    <span className="text-sm">Güvenli ödeme & 30 gün para iade garantisi</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
          
          {/* Plan Features */}
          <div className="mt-16">
            <h3 className={`text-xl md:text-2xl font-bold mb-8 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Planlarımızın İçerdiği Özellikler
            </h3>
            
            <div className="overflow-x-auto">
              <table className={`w-full min-w-[800px] ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                <thead>
                  <tr className={isDark ? 'border-b border-slate-700' : 'border-b border-slate-200'}>
                    <th className="py-4 px-4 text-left">Özellik</th>
                    <th className="py-4 px-4 text-center">İş Takip</th>
                    <th className="py-4 px-4 text-center">Stok Yönetimi</th>
                    <th className="py-4 px-4 text-center">Tam Paket</th>
                  </tr>
                </thead>
                <tbody>
                  <FeatureRow 
                    feature="Sınırsız projeler" 
                    taskManagement={true}
                    stockManagement={false}
                    completeSuite={true}
                    isDark={isDark}
                  />
                  <FeatureRow 
                    feature="Gerçek zamanlı raporlama" 
                    taskManagement={true}
                    stockManagement={true}
                    completeSuite={true}
                    isDark={isDark}
                  />
                  <FeatureRow 
                    feature="Çoklu dil desteği" 
                    taskManagement={true}
                    stockManagement={true}
                    completeSuite={true}
                    isDark={isDark}
                  />
                  <FeatureRow 
                    feature="Envanter takibi" 
                    taskManagement={false}
                    stockManagement={true}
                    completeSuite={true}
                    isDark={isDark}
                  />
                  <FeatureRow 
                    feature="QR/Barkod tarama" 
                    taskManagement={false}
                    stockManagement={true}
                    completeSuite={true}
                    isDark={isDark}
                  />
                  <FeatureRow 
                    feature="Görev otomasyon" 
                    taskManagement={true}
                    stockManagement={false}
                    completeSuite={true}
                    isDark={isDark}
                  />
                  <FeatureRow 
                    feature="Stok alarmları" 
                    taskManagement={false}
                    stockManagement={true}
                    completeSuite={true}
                    isDark={isDark}
                  />
                  <FeatureRow 
                    feature="API entegrasyonu" 
                    taskManagement={true}
                    stockManagement={true}
                    completeSuite={true}
                    isDark={isDark}
                  />
                  <FeatureRow 
                    feature="Gelişmiş raporlar" 
                    taskManagement={true}
                    stockManagement={true}
                    completeSuite={true}
                    isDark={isDark}
                  />
                </tbody>
              </table>
            </div>
            
            <div className="mt-12 text-center">
              <p className={`mb-6 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                Özel ihtiyaçlarınız mı var? Size özel çözümler için bizimle iletişime geçin.
              </p>
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                className={`py-3 px-8 rounded-xl font-medium inline-flex items-center ${
                  isDark 
                    ? 'bg-white/10 text-white hover:bg-white/15 border border-white/10' 
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200'
                }`}
              >
                <MessageCircle className="mr-2 w-5 h-5" />
                Satış Ekibiyle Görüşün
              </motion.button>
            </div>
          </div>
        </div>
      </section>
      
      {/* About Us Section */}
      <section id="about" className="py-20 md:py-28 relative">
        {/* Background decoration elements */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className={`absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:40px_40px] ${isDark ? 'opacity-10' : 'opacity-15'}`}></div>
          
          <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-blue-500/5 blur-3xl"></div>
          <div className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-indigo-500/5 blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Section Header */}
          <div className="max-w-3xl mx-auto text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-purple-500 to-pink-400">Hikayemiz</span> ve Misyonumuz
              </h2>
              <p className={`text-lg ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Kurumsal iş yönetimi dünyasında yenilikçi çözümler sunma tutkusuyla doğduk.
              </p>
            </motion.div>
          </div>
          
          {/* Company Story and Values */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
            {/* Left Column - Company Story */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <TitleGradient>
                  Beetrax'ın Doğuşu
                </TitleGradient>
              </h3>
              
              <div className={`space-y-5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <p>
                  <span className="font-medium">2025 yılında</span>, işletmelerin karşılaştığı büyük 
                  zorlukları çözmek ve iş süreçlerini optimize etmek amacıyla Beetrax'ı kurduk. 
                  Sürekli değişen iş dünyasında, şirketlerin daha verimli çalışabilmesi için yenilikçi 
                  çözümler sunma hedefiyle yola çıktık.
                </p>
                
                <p>
                  İlk günden bu yana, kullanıcılarımızın gerçek ihtiyaçlarını dinleyerek 
                  ve en son teknolojik yenilikleri takip ederek platformumuzu geliştiriyoruz. 
                  Bugün, binlerce işletmenin günlük iş süreçlerini yönettiği güvenilir bir 
                  çözüm ortağı haline geldik.
                </p>
                
                <p>
                  <span className="font-medium">Vizyonumuz</span>, işletmelerin potansiyellerini 
                  tam anlamıyla gerçekleştirmelerine yardımcı olan, erişilebilir ve kullanımı kolay 
                  yazılım çözümleri sunmaya devam etmektir.
                </p>
                
                <p>
                  <span className="font-medium">Beetrax platformu</span>, sadece iş takip sistemi değil, 
                  aynı zamanda gelişmiş bir <strong>stok yönetimi sistemi</strong> de sunmaktadır. 
                  Envanter takibi, tedarik zinciri yönetimi, otomatik stok uyarıları ve barcode/QR kod 
                  entegrasyonu ile işletmeler tüm operasyonel süreçlerini tek bir platform üzerinden 
                  yönetebilir.
                </p>
                
                <p>
                  <span className="font-medium">Akıllı stok yönetimi özelliklerimiz</span> sayesinde, 
                  stok seviyelerini gerçek zamanlı takip edebilir, kritik stok eşiklerinde otomatik 
                  bildirimler alabilir ve tedarikçi süreçlerinizi optimize edebilirsiniz. Hem küçük 
                  işletmeler hem de büyük kurumsal firmalar için ölçeklenebilir çözümler sunuyoruz.
                </p>
              </div>
            </motion.div>
            
            {/* Right Column - Company Image */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className={`absolute inset-0 rounded-3xl transform rotate-3 ${isDark ? 'bg-blue-900/20' : 'bg-blue-100/70'}`}></div>
              <div className={`absolute inset-0 rounded-3xl transform -rotate-3 ${isDark ? 'bg-indigo-900/20' : 'bg-indigo-100/70'}`}></div>
              
              <div className="relative overflow-hidden rounded-3xl shadow-xl">
                <img 
                  src="https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/beetrax-banner.png"
                  alt="Beetrax Ekibi"
                  className="w-full h-full object-cover"
                />
              </div>
              
              <div className={`absolute -bottom-5 -right-5 p-4 rounded-xl shadow-lg backdrop-blur-lg ${
                isDark ? 'bg-slate-800/90 text-white border border-slate-700' : 'bg-white text-slate-900 border border-slate-200'
              }`}>
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-blue-500 p-2">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold">20+ Uzman Ekip Üyesi</p>
                    <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Alanında uzman profesyoneller</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
          
          {/* Core Values Section */}
          <div className="mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h3 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <TitleGradient>
                  Değerlerimiz
                </TitleGradient>
              </h3>
              <p className={`max-w-2xl mx-auto ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Başarımızın temeli, her kararımızı ve her ürün geliştirmemizi yönlendiren temel değerlerimizdir.
              </p>
            </motion.div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: <Gem className="h-6 w-6" />,
                  title: "Kalite",
                  description: "Her detayda mükemmelliği hedefliyor, kullanıcılarımıza en yüksek kalitede deneyim sunuyoruz."
                },
                {
                  icon: <Lightbulb className="h-6 w-6" />,
                  title: "İnovasyon",
                  description: "Sürekli yeni fikirler ve çözümler geliştirerek, sektörde öncü olmaya devam ediyoruz."
                },
                {
                  icon: <Heart className="h-6 w-6" />,
                  title: "Kullanıcı Odaklılık",
                  description: "Her kararımızda kullanıcılarımızın ihtiyaçlarını ve deneyimlerini ön planda tutuyoruz."
                },
                {
                  icon: <Shield className="h-6 w-6" />,
                  title: "Güvenilirlik",
                  description: "Veri güvenliği ve sistem kararlılığı konusunda taviz vermeden çalışıyoruz."
                },
                {
                  icon: <RefreshCw className="h-6 w-6" />,
                  title: "Sürekli Gelişim",
                  description: "Her gün daha iyisini yapmak için çalışıyor, platformumuzu sürekli geliştiriyoruz."
                },
                {
                  icon: <Globe className="h-6 w-6" />,
                  title: "Erişilebilirlik",
                  description: "Herkesin kaliteli yazılım çözümlerine erişebilmesi için çalışıyoruz."
                }
              ].map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`p-6 rounded-xl ${
                    isDark 
                      ? 'bg-slate-800/50 border border-slate-700/50' 
                      : 'bg-white border border-slate-100 shadow-sm'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-lg mb-4 flex items-center justify-center ${
                    isDark 
                      ? 'bg-gradient-to-br from-blue-900/50 to-indigo-900/50 text-white' 
                      : 'bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-600'
                  }`}>
                    {value.icon}
                  </div>
                  <h4 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{value.title}</h4>
                  <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
          
          {/* Testimonial or Mission Statement */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className={`rounded-3xl overflow-hidden relative ${
              isDark 
                ? 'bg-gradient-to-r from-blue-900/20 to-indigo-900/20 border border-slate-700/50' 
                : 'bg-gradient-to-r from-blue-50 to-indigo-50'
            }`}
          >
            <div className="absolute inset-0 overflow-hidden">
              <div className={`absolute top-0 left-0 w-1/2 h-1/2 opacity-30 bg-[radial-gradient(circle,rgba(120,120,120,0.1)_1px,transparent_1px)] bg-[size:12px_12px]`}></div>
              <div className={`absolute bottom-0 right-0 w-1/2 h-1/2 opacity-30 bg-[radial-gradient(circle,rgba(120,120,120,0.1)_1px,transparent_1px)] bg-[size:12px_12px]`}></div>
            </div>
            
            <div className="relative p-8 md:p-12 text-center">
              <h3 className={`text-2xl md:text-3xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <TitleGradient>
                  "İş verimliliğini artırmak için her gün çalışıyoruz"
                </TitleGradient>
              </h3>
              <p className={`text-lg max-w-3xl mx-auto mb-10 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Misyonumuz, işletmelerin karmaşık iş süreçlerini basitleştirmek, ekiplerin iş birliğini 
                güçlendirmek ve organizasyonların veri odaklı kararlar almasını sağlamaktır. 
                Beetrax olarak, müşterilerimizin başarısı bizim başarımızdır.
              </p>
              
              <div className="flex justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/contact')}
                  className={`py-4 px-8 rounded-xl font-medium ${
                    isDark 
                      ? 'bg-white text-slate-900 hover:bg-gray-100' 
                      : 'bg-slate-900 text-white hover:bg-slate-800'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <ChevronsRight className="w-5 h-5" />
                    <span>Bizimle İletişime Geçin</span>
                  </div>
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer Section */}
      <footer className={` py-12 px-4 border-t ${isDark ? 'border-gray-800 bg-gray-900/30' : 'border-gray-200 bg-gray-50/50'}`}>
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="md:col-span-1">
              <a href="#">              
              <img 
              src={currentLogo} 
              alt="Beetrax Logo" 
              className="w-[130px] h-[30px] object-contain mb-4"
              />
            </a>
              <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                İş süreçlerinizi modernize eden, ekip iş birliğini güçlendiren ve veri odaklı karar almanızı sağlayan gelişmiş iş yönetim platformu.
              </p>
              <div className="flex space-x-4">
                <a href="#" className={`text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                  <Twitter className="h-5 w-5" />
                </a>
                <a href="#" className={`text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                  <Linkedin className="h-5 w-5" />
                </a>
                <a href="#" className={`text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                  <Github className="h-5 w-5" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Hızlı Bağlantılar</h4>
              <ul className="space-y-2">
                {['Ana Sayfa', 'Özellikler', 'Fiyatlandırma', 'Hakkımızda', 'İletişim'].map((item, index) => (
                  <li key={index}>
                    <a 
                      href={(() => {
                        // Map Turkish names to English section IDs or external routes
                        const sectionMap = {
                          'Ana Sayfa': '#home',
                          'Özellikler': '#features',
                          'Fiyatlandırma': '#pricing',
                          'Hakkımızda': '#about',
                          'İletişim': '/contact'
                        };
                        return sectionMap[item] || `#${item.toLowerCase().replace(' ', '-')}`;
                      })()}
                      onClick={(e) => {
                        e.preventDefault();
                        
                        // Special handling for contact page
                        if (item === 'İletişim') {
                          navigate('/contact');
                          return;
                        }
                        
                        const sectionId = e.target.getAttribute('href').substring(1);
                        const section = document.getElementById(sectionId);
                        
                        if (section) {
                          // Use Lenis smooth scroll if available
                          const lenis = getLenis();
                          if (lenis) {
                            lenis.scrollTo(section, { offset: 0, duration: 1.2, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
                          } else {
                            // Fallback to regular scroll
                            window.scrollTo({
                              top: section.offsetTop,
                              behavior: 'smooth'
                            });
                          }
                        }
                      }}
                      className={`text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                    >
                      {item}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h4 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Kaynaklar</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/documentation" className={`text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                    Dokümantasyon
                  </Link>
                </li>
                <li>
                  <Link to="/tutorials" className={`text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                    Eğitimler
                  </Link>
                </li>
                <li>
                    <a href="/blog" className={`text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                    Blog
                    </a>
                  </li>
                <li>
                  <Link to="/help-support" className={`text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                    Yardım Merkezi
                  </Link>
                </li>
               
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>İletişim</h4>
              <ul className="space-y-2">
                <li className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  <span className="block">Email: info@beetrax.net</span>
                </li>
                <li className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  <span className="block">Telefon: +90 (552) 424 11 08</span>
                </li>
                <li className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  <span className="block">Adres: Muallimköy, Deniz Cd. No:143-5, 41400 Gebze/Kocaeli</span>
                </li>
              </ul>
            </div>
          </div>

          <div className={`mt-12 pt-8 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                &copy; {new Date().getFullYear()} Beetrax.net Tüm hakları saklıdır.
              </p>
              <div className="flex space-x-4 mt-4 md:mt-0">
                <a href="/privacy-policy" className={`text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                  Gizlilik Politikası
                </a>
                <a href="/terms-of-service" className={`text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                  Kullanım Şartları
                </a>
                <a href="/kvkk" className={`text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                  KVKK
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;